import { Moment } from 'moment';
import {
	AccountTypes,
	FixtureCounterpartyTypes,
	BankAccountTypes,
	FixtureTypes,
	VesselOwnershipTypes,
	FuelTypes,
	Currencies,
	DepartmentKey,
	AccrualItemClosingPeriodFormats,
	AttachmentTypes,
	CustomFieldType,
	CustomFieldParentTypes,
	AccountingSystemStrategies,
	VesselTypes,
	CapexOpexInput,
	DashboardPeriods,
	BareboatItemType,
	VoyagePaymentTypes,
	ExportTypes,
	ExpensesSubjectToHireDaysIntervals,
	DaysDashboardPeriods,
	LinerCostUnitDefaults,
	TimeZoneFormat,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import HireInvoiceItem from '@shared/hireInvoice/HireInvoiceItem';
import { formatDate } from '@shared/utils/date';
import type {
	SimpleBunker,
	HandleEstimateCommencementBunkersRequest,
	HandleEstimateCommencementBunkersResponse,
} from '@api/features/estimates/handleEstimateCommencementBunkers';
import type {
	RemoveCargoFromContractRequest,
	RemoveCargoFromContractResponse,
} from '@api/features/cargos/removeCargoFromContract';
import type {
	DeleteBunkerStemRequest,
	DeleteBunkerStemResponse,
} from '@api/features/voyages/bunker-stems/deleteBunkerStem';
import type {
	ExportOffHireDataRequest,
	ExportOffHireDataResponse,
} from '@api/features/statistics/off-hire/exportOffHireData';
import type {
	GetEstimateGroupRequest,
	GetEstimateGroupResponse,
} from '@api/features/estimates/getEstimateGroup';
import type {
	GetEstimateGroupVesselsRequest,
	GetEstimateGroupVesselsResponse,
} from '@api/features/estimates/getEstimateGroupVessels';
import type {
	GetEstimateGroupCargosRequest,
	GetEstimateGroupCargosResponse,
} from '@api/features/estimates/getEstimateGroupCargos';
import type {
	GetContractLinkMissingDataRequest,
	GetContractLinkMissingDataResponse,
} from '@api/features/estimates/getContractLinkMissingData';
import {
	CreateOrgCanalDefaultsRequest,
	CreateOrgCanalDefaultsResponse,
} from '@api/features/organizations/canal-defaults/createOrgCanalTransitDefaults';
import type {
	GetVesselsRequest,
	GetVesselsResponse,
} from '@api/features/vessels/getVessels';
import type { DeleteFixtureRequest } from '@api/features/fixtures/deleteFixture';
import type {
	GetVesselDetailsRequest,
	GetVesselDetailsResponse,
} from '@api/features/vessels/getVesselDetails';
import type {
	GetDaDeskPortCallInfoRequest,
	GetDaDeskPortCallInfoResponse,
} from '@api/features/ops/getDaDeskPortCallInfo';
import type {
	CalculateRequiredArrivalSpeedRequest,
	CalculateRequiredArrivalSpeedResponse,
} from '@api/features/vessels/calculateRequiredArrivalSpeed';
import type {
	GetVoyagesRequest,
	GetVoyagesResponse,
} from '@api/features/voyages/getVoyages';
import type {
	UpdatePeriodScheduleItemRequest,
	UpdatePeriodScheduleItemResponse,
} from '@api/features/fixtures/updatePeriodScheduleItem';
import type {
	DeletePeriodScheduleItemRequest,
	DeletePeriodScheduleItemResponse,
} from '@api/features/fixtures/deletePeriodScheduleItem';
import type {
	CreatePeriodScheduleItemResponse,
	CreatePeriodScheduleItemRequest,
} from '@api/features/fixtures/createPeriodScheduleItem';
import type {
	GetVesselsWithPositionsRequest,
	GetVesselsWithPositionsResponse,
} from '@api/features/geo/getVesselsWithPositions';
import type {
	GetPortsInBoundsRequest,
	GetPortsInBoundsResponse,
} from '@api/features/geo/getPortsInBounds';
import type {
	UpdateHireInvoiceDetailsRequest,
	UpdateHireInvoiceDetailsResponse,
} from '@api/features/invoices/hire-invoices/updateHireInvoiceDetails';
import type {
	GetHireInvoiceDetailsRequest,
	GetHireInvoiceDetailsResponse,
} from '@api/features/invoices/hire-invoices/getHireInvoiceDetails';
import type {
	GetThirdPartyReportsRequest,
	GetThirdPartyReportsResponse,
} from '@api/features/vessels/getThirdPartyReports';
import type {
	CreateVesselRequest,
	CreateVesselResponse,
} from '@api/features/vessels/createVessel';
import type {
	CreateFixtureRequest,
	CreateFixtureResponse,
} from '@api/features/fixtures/createFixture';
import type {
	CreateFixtureExpenseRequest,
	CreateFixtureExpenseResponse,
} from '@api/features/fixtures/createFixtureExpense';
import type {
	CreateSupplierRequest,
	CreateSupplierResponse,
} from '@api/features/suppliers/createSupplier';
import type {
	GetAttachmentUrlRequest,
	GetAttachmentUrlResponse,
} from '@api/features/attachments/getAttachmentUrl';
import type {
	GetTcInAllocationItemsRequest,
	GetTcInAllocationItemsResponse,
} from '@api/features/voyages/getTcInAllocationItems';
import type {
	GetVoyageHireItemsRequest,
	GetVoyageHireItemsResponse,
} from '@api/features/voyages/getVoyageHireItems';
import type {
	UpdateHireItemRequest,
	UpdateHireItemResponse,
} from '@api/features/voyages/updateHireItem';
import type {
	UpdateTcInAllocationItemRequest,
	UpdateTcInAllocationItemResponse,
} from '@api/features/voyages/updateTcInAllocationItem';
import type {
	CreateVoyageBunkersRequest,
	CreateVoyageBunkersResponse,
} from '@api/features/voyages/createVoyageBunkers';
import type {
	DeleteFixtureExpenseRequest,
	DeleteFixtureExpenseResponse,
} from '@api/features/fixtures/deleteFixtureExpense';
import type {
	CalculateLaytimeRequest,
	CalculateLaytimeResponse,
} from '@api/features/laytime/calculateLaytime';
import type {
	CopyLaytimeToCargoReletRequest,
	CopyLaytimeToCargoReletResponse,
} from '@api/features/laytime/copyLaytimeToCargoRelet';
import type {
	GetCounterpartiesRequest,
	GetCounterpartiesResponse,
} from '@api/features/counterparties/getCounterparties';
import type {
	DeleteSupplierRequest,
	DeleteSupplierResponse,
} from '@api/features/suppliers/deleteSupplier';
import type {
	DeleteVoyageBunkersRequest,
	DeleteVoyageBunkersResponse,
} from '@api/features/voyages/deleteVoyageBunkers';
import type {
	GetOffHireDataRequest,
	GetOffHireDataResponse,
} from '@api/features/statistics/off-hire/getOffHireData';
import type {
	GetSuppliersRequest,
	GetSuppliersResponse,
} from '@api/features/suppliers/getSuppliers';
import type {
	UpdateFixtureExpenseRequest,
	UpdateFixtureExpenseResponse,
} from '@api/features/fixtures/updateFixtureExpense';
import type {
	GetPnlOverrideRequest,
	GetPnlOverrideResponse
} from '@api/features/voyages/pnl-override/getPnlOverride';
import type {
	UpdatePnlOverrideRequest,
	UpdatePnlOverrideResponse
} from '@api/features/voyages/pnl-override/updatePnlOverride';
import type {
	UpdateSupplierRequest,
	UpdateSupplierResponse,
} from '@api/features/suppliers/updateSupplier';
import type {
	UpdateCounterpartyRequest,
	UpdateCounterpartyResponse,
} from '@api/features/counterparties/updateCounterparty';
import type {
	UpdateVoyageBunkersRequest,
	UpdateVoyageBunkersResponse,
} from '@api/features/voyages/updateVoyageBunkers';
import type {
	UpdateHireInvoiceRequest,
	UpdateHireInvoiceResponse,
} from '@api/features/voyages/updateHireInvoice';
import type {
	UpdateVoyagePaymentRequest,
	UpdateVoyagePaymentResponse,
} from '@api/features/voyages/updateVoyagePayment';
import type {
	UpdateVoyageExpensePaymentRequest,
	UpdateVoyageExpensePaymentResponse,
} from '@api/features/voyages/updateVoyageExpensePayment';
import type {
	UpdateOffHirePeriodAttributes,
	UpdateOffHirePeriodRequest,
	UpdateOffHirePeriodResponse,
} from '@api/features/voyages/updateVoyageOffHirePeriod';
import type {
	GetUserDataRequest,
	GetUserDataResponse,
} from '@api/features/user/getUserData';
import type {
	GetConnectionAndMappingRequest,
	GetConnectionAndMappingResponse,
} from '@api/features/accounting-system/getConnectionAndMapping';
import type {
	GetAccountingCustomersRequest,
	GetAccountingCustomersResponse,
} from '@api/features/accounting-system/getAccountingCustomers';
import type {
	GetCompanyAccountsRequest,
	GetCompanyAccountsResponse,
} from '@api/features/accounting-system/getCompanyAccounts';
import type {
	GetAccountingSuppliersRequest,
	GetAccountingSuppliersResponse,
} from '@api/features/accounting-system/getAccountingSuppliers';
import type {
	CreateVesselOffHireRequest,
	CreateVesselOffHireResponse,
} from '@api/features/vessels/createVesselOffHirePeriod';
import type {
	CreateConnectionRequest,
	CreateConnectionResponse,
} from '@api/features/accounting-system/createConnection';
import type {
	GetAccountingCompaniesRequest,
	GetAccountingCompaniesResponse,
} from '@api/features/accounting-system/getAccountingCompanies';
import type {
	DisconnectAccountingSystemRequest,
	DisconnectAccountingSystemResponse,
} from '@api/features/accounting-system/disconnectAccountingSystem';
import type {
	CreateMappingEntryRequest,
	CreateMappingEntryResponse,
} from '@api/features/accounting-system/createMappingEntry';
import type {
	RefreshEuaPriceRequest,
	RefreshEuaPriceResponse,
} from '@api/features/estimates/refreshEuaPrice';
import type {
	UpdateVesselOffHirePeriodRequest,
	UpdateVesselOffHirePeriodResponse,
} from '@api/features/vessels/updateVesselOffHirePeriod';
import type {
	CreateVoyagePaymentRequest,
	CreateVoyagePaymentResponse,
} from '@api/features/voyages/createVoyagePayment';
import type {
	CreateVoyageExpensePaymentRequest,
	CreateVoyageExpensePaymentResponse,
} from '@api/features/voyages/createVoyageExpensePayment';
import type {
	DeleteVoyageExpensePaymentRequest,
	DeleteVoyageExpensePaymentResponse,
} from '@api/features/voyages/deleteVoyageExpensePayment';
import type {
	GetVoyageExpensePaymentsRequest,
	GetVoyageExpensePaymentsResponse,
} from '@api/features/voyages/getVoyageExpensePayments';
import type {
	DeleteVoyagePaymentRequest,
	DeleteVoyagePaymentResponse,
} from '@api/features/voyages/deleteVoyagePayment';
import type {
	CreateVesselBunkerRequest,
	CreateVesselBunkerResponse,
} from '@api/features/vessels/createVesselBunker';
import type {
	UpdateVesselBunkerRequest,
	UpdateVesselBunkerResponse,
} from '@api/features/vessels/updateVesselBunker';
import type {
	DeleteVesselOffHirePeriodRequest,
	DeleteVesselOffHirePeriodResponse,
} from '@api/features/vessels/deleteVesselOffHirePeriod';
import type {
	AllocateVesselOffHirePeriodRequest,
	AllocateVesselOffHirePeriodResponse,
} from '@api/features/vessels/allocateVesselOffHirePeriod';
import type {
	UnAllocateVesselOffHirePeriodRequest,
	UnAllocateVesselOffHirePeriodResponse,
} from '@api/features/vessels/unAllocateVesselOffHirePeriod';
import type {
	DeleteVesselBunkerRequest,
	DeleteVesselBunkerResponse,
} from '@api/features/vessels/deleteVesselBunker';
import type {
	DeleteItineraryEntryRequest,
	DeleteItineraryEntryResponse,
} from '@api/features/ops/deleteItineraryEntry';
import type {
	UpdatePortCallRequest,
	UpdatePortCallResponse,
} from '@api/features/ops/updatePortCall';
import type {
	DeletePortCallActionRequest,
	DeletePortCallActionResponse,
} from '@api/features/ops/deletePortCallAction';
import type {
	DeletePortCallActivityRequest,
	DeletePortCallActivityResponse,
} from '@api/features/ops/deletePortCallActivity';
import type {
	UpdatePortCallActionRequest,
	UpdatePortCallActionResponse,
} from '@api/features/ops/updatePortCallAction';
import type {
	UpdatePortCallActivityRequest,
	UpdatePortCallActivityResponse,
} from '@api/features/ops/updatePortCallActivity';
import type {
	CreatePortCallActivityRequest,
	CreatePortCallActivityResponse,
} from '@api/features/ops/createPortCallActivity';
import type {
	UpdateSeaPassageRequest,
	UpdateSeaPassageResponse,
} from '@api/features/ops/updateSeaPassage';
import type {
	CreateItineraryEntryRequest,
	CreateItineraryEntryResponse,
} from '@api/features/ops/createItineraryEntry';
import type {
	CalculateMultiRouteRequest,
	CalculateMultiRouteResponse,
} from '@api/features/geo/calculateMultiRoute';
import type {
	GetOrganizationDetailsRequest,
	GetOrganizationDetailsResponse,
} from '@api/features/admin/getOrganizationDetails';
import type {
	Port,
	Range,
} from '@api/utils/ports';
import type {
	GetEstimateDetailsRequest,
	GetEstimateDetailsResponse,
} from '@api/features/estimates/getEstimateDetails';
import type {
	UpdateExpenseSubjectToHireDaysRequest,
	UpdateExpenseSubjectToHireDaysResponse,
} from '@api/features/fixtures/updateExpenseSubjectToHireDays';
import type {
	CreateFuelEuRequest,
	GetFuelEuResponse,
} from '@api/features/estimates/utils/addFuelEuPenalty';
import type { DeleteFuelEuCalculationRequest } from '@api/features/estimates/utils/deleteFuelCalculation';
import type { UpdateFuelEuCalculationRequest } from '@api/features/estimates/utils/updateFuelEuPenalty';
import type {
	GetFuelEuCalculationDetailsRequest,
	GetFuelEuDelatilsResponse,
} from '@api/features/estimates/utils/getFuelEuCalculations';
import type {
	CreateEstimateCostAttributes,
	CreateEstimateCostRequest,
	CreateEstimateCostResponse,
} from '@api/features/estimates/utils/addEstimateCost';
import type {
	GetEstimateCostsRequest,
	GetEstimateCostsResponse,
} from '@api/features/estimates/utils/getEstimateCosts';
import type {
	UpdateEstimateCostAttributes,
	UpdateEstimateCostRequest,
	UpdateEstimateCostResponse,
} from '@api/features/estimates/utils/updateEstimateCost';
import type {
	DeleteEstimateCostRequest,
	DeleteEstimateCostResponse,
} from '@api/features/estimates/utils/deleteEstimateCost';
import type {
	GetEstimatedBrokerCommissionsRequest,
	GetEstimatedBrokerCommissionsResponse,
} from '@api/features/estimates/getEstimatedBrokerCommissions';
import type {
	GetEstimatedDespatchAndDemurrageRequest,
	GetEstimatedDespatchAndDemurrageResponse,
} from '@api/features/estimates/getEstimatedDespatchAndDemurrage';
import type {
	AddCargoToEstimateRequest,
	AddCargoToEstimateResponse,
} from '@api/features/estimates/addCargoToEstimate';
import type {
	UpdateEstimateRequest,
	UpdateEstimateResponse,
} from '@api/features/estimates/updateEstimate';
import type {
	GetEstimatesRequest,
	GetEstimatesResponse,
} from '@api/features/estimates/getEstimates';
import type {
	GetCargosResponse,
	GetCargosRequest,
} from '@api/features/cargos/getCargos';
import type {
	CreateEstimateRequest,
	CreateEstimateResponse,
} from '@api/features/estimates/createEstimate';
import type {
	CreateContractFromEstimateRequest,
	CreateContractFromEstimateResponse,
} from '@api/features/estimates/createContractFromEstimate';
import type {
	DeleteEstimateRequest,
	DeleteEstimateResponse,
} from '@api/features/estimates/deleteEstimate';
import type {
	DeleteEstimateGroupRequest,
	DeleteEstimateGroupResponse,
} from '@api/features/estimates/deleteEstimateGroup';
import type {
	CreateSubEstimateRequest,
	CreateSubEstimateResponse,
} from '@api/features/estimates/createSubEstimate';
import type {
	CopyEstimateRequest,
	CopyEstimateResponse,
} from '@api/features/estimates/copyEstimate';
import type {
	GetFixtureDetailsRequest,
	GetFixtureDetailsResponse,
} from '@api/features/fixtures/getFixtureDetails';
import type { SpotFixtureProps } from '@api/models/spot-fixture';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type {
	GetBrokersRequest,
	GetBrokersResponse,
} from '@api/features/brokers/getBrokers';
import type {
	GetVoyageCumulativeRequest,
	GetVoyageCumulativeResponse,
} from '@api/features/voyages/getVoyageCumulative';
import type {
	GetVesselBankAccountsRequest,
	GetVesselBankAccountsResponse,
} from '@api/features/vessels/getVesselBankAccounts';
import type {
	CreateBrokerRequest,
	CreateBrokerResponse,
} from '@api/features/brokers/createBroker';
import type {
	GetFixturesRequest,
	GetFixturesResponse,
} from '@api/features/fixtures/getFixtures';
import type {
	PostAccountingItemsRequest as ApiPostAccountingItemsRequest,
} from '@api/features/financials/postAccountingItems';
import type {
	ArchiveAccountingItemRequest,
	ArchiveAccountingItemResponse,
} from '@api/features/financials/archiveAccountingItem';
import type {
	CloseAccountingItemsRequest,
	CloseAccountingItemsResponse,
} from '@api/features/financials/closeAccountingItems';
import type {
	PreviewFixtureRecapRequest,
	PreviewFixtureRecapResponse,
} from '@api/features/fixtures/previewFixtureRecap';
import type {
	GetExportRequest,
	GetExportResponse,
	InvoiceExportAttributes,
} from '@api/features/misc/getExport';
import type {
	CreateFixtureRecapRequest,
	CreateFixtureRecapResponse,
} from '@api/features/fixtures/createFixtureRecap';
import type {
	GetVesselActivityRequest,
	GetVesselActivityResponse,
} from '@api/features/vessels/getVesselActivity';
import type {
	ReorderPortCallRequest,
	ReorderPortCallResponse,
} from '@api/features/ops/reorderPortCall';
import type {
	GetVoyageDetailsRequest,
	GetVoyageDetailsResponse,
} from '@api/features/voyages/getVoyageDetails';
import type {
	GetVoyageCompletionTypeRequest,
	GetVoyageCompletionTypeResponse,
} from '@api/features/voyages/getVoyageCompletionType';
import type {
	CopyCargoRequest,
	CopyCargoResponse,
} from '@api/features/cargos/copyCargo';
import type {
	GetCargoDetailsRequest,
	GetCargoDetailsResponse,
} from '@api/features/cargos/getCargoDetails';
import type {
	CreateCargoRequest,
	CreateCargoResponse,
} from '@api/features/cargos/createCargo';
import type {
	CreateCargoReletRequest,
	CreateCargoReletResponse,
} from '@api/features/cargos/createCargoRelet';
import type {
	UpdateCargoRequest,
	UpdateCargoResponse,
} from '@api/features/cargos/updateCargo';
import type {
	UpdateRobRequest,
	UpdateRobResponse,
} from '@api/features/vessels/updateRob';
import type {
	UpdateRobBatchRequest,
	UpdateRobBatchResponse,
} from '@api/features/vessels/updateRobBatch';
import type {
	DeleteRobBunkerRequest,
	DeleteRobBunkerResponse,
} from '@api/features/vessels/deleteRobBunker';
import type {
	UpdateCargoPortRequest,
	UpdateCargoPortResponse,
} from '@api/features/cargos/updateCargoPort';
import type {
	GetEstimateGroupsRequest,
	GetEstimateGroupsResponse,
} from '@api/features/estimates/getEstimateGroups';
import type {
	DeleteVoyageExpenseReceivableRequest,
	DeleteVoyageExpenseReceivableResponse,
} from '@api/features/voyages/deleteVoyageExpenseReceivable';
import type {
	UpdateVoyageExpenseReceivableRequest,
	UpdateVoyageExpenseReceivableResponse,
} from '@api/features/voyages/updateVoyageExpenseReceivable';
import type {
	DeleteAttachmentRequest,
	DeleteAttachmentResponse,
} from '@api/features/attachments/deleteAttachment';
import type {
	GetOrgBankAccountsRequest,
	GetOrgBankAccountsResponse,
} from '@api/features/organizations/getOrgBankAccounts';
import type {
	UpdateBankAccountRequest,
	UpdateBankAccountResponse,
} from '@api/features/bank-accounts/updateBankAccount';
import type {
	UpdateFixtureAttributes,
	UpdateFixtureRequest,
	UpdateFixtureResponse,
} from '@api/features/fixtures/updateFixture';
import type {
	UpdateTcTermsRequest,
	UpdateTcTermsResponse,
} from '@api/features/fixtures/updateTcTerms';
import type {
	CreateBankAccountRequest,
	CreateBankAccountResponse,
} from '@api/features/bank-accounts/createBankAccount';
import type {
	UpdateUserRequest,
	UpdateUserResponse,
	UserAttributes,
} from '@api/features/user/updateUser';
import type {
	GetRobsRequest,
	GetRobsResponse,
} from '@api/features/vessels/getRobs';
import type {
	GetRobsFromPortCallRequest,
	GetRobsFromPortCallResponse,
} from '@api/features/vessels/getRobsFromPortCall';
import type {
	GetTheShipSeaPassageDetailsRequest,
	GetTheShipSeaPassageDetailsResponse,
} from '@api/features/vessels/getTheShipSeaPassageDetails';
import type {
	CreateRobsRequest,
	CreateRobsResponse,
} from '@api/features/vessels/createRob';
import type {
	DeleteBankAccountRequest,
	DeleteBankAccountResponse,
} from '@api/features/bank-accounts/deleteBankAccount';
import type {
	GetCargosAndLaytimesRequest,
	GetCargosAndLaytimesResponse,
} from '@api/features/laytime/getCargosAndLaytimes';
import type {
	GetVoyageExpensesRequest,
	GetVoyageExpensesResponse,
} from '@api/features/invoices/getVoyageExpenses';
import type {
	GetVoyageExpenseRequest,
	GetVoyageExpenseResponse,
} from '@api/features/invoices/getVoyageExpense';
import type {
	CreateHireInvoiceRequest,
	CreateHireInvoiceResponse,
} from '@api/features/invoices/hire-invoices/createHireInvoice';
import type {
	RejectExpenseRequest,
	RejectExpenseResponse,
} from '@api/features/invoices/rejectExpense';
import type {
	ApproveExpenseRequest,
	ApproveExpenseResponse,
} from '@api/features/invoices/approveExpense';
import type {
	SubmitDisbursementAccountRequest,
	SubmitDisbursementAccountResponse,
} from '@api/features/disbursement-accounts/approvals/submitDisbursementAccount';
import type {
	UnsubmitDisbursementAccountRequest,
	UnsubmitDisbursementAccountResponse,
} from '@api/features/disbursement-accounts/approvals/unsubmitDisbursementAccount';
import type {
	ApproveDisbursementAccountRequest,
	ApproveDisbursementAccountResponse,
} from '@api/features/disbursement-accounts/approvals/approveDisbursementAccount';
import type {
	RejectDisbursementAccountRequest,
	RejectDisbursementAccountResponse,
} from '@api/features/disbursement-accounts/approvals/rejectDisbursementAccount';
import type {
	UpdateVoyageAttributes,
	UpdateVoyageRequest,
	UpdateVoyageResponse,
} from '@api/features/voyages/updateVoyage';
import type {
	SyncToTheShipRequest,
	SyncToTheShipResponse,
} from '@api/features/voyages/syncToTheShip';
import type {
	FetchFromTsRequest,
	FetchFromTsResponse,
} from '@api/features/voyages/fetchFromTs';
import type {
	GetVcPnlRequest,
	GetVcPnlResponse,
} from '@api/features/voyages/getVcPnl';
import type {
	GetTcPnlRequest,
	GetTcPnlResponse,
} from '@api/features/voyages/getTcPnl';
import type {
	UpdateOrganizationDetailsRequest,
	UpdateOrganizationDetailsResponse,
} from '@api/features/organizations/updateOrganizationDetails';
import type { ApprovalItem, PortPurposeItem } from '@api/models/organization';
import type {
	GetPortPurposesRequest,
	GetPortPurposesResponse,
} from '@api/features/organizations/getPortPurposeList';
import type {
	GetRequiredApprovalsRequest,
	GetRequiredApprovalsResponse,
} from '@api/features/organizations/getRequiredApprovals';
import type {
	GetAccountingItemsForApprovalRequest,
	GetAccountingItemsForApprovalResponse,
} from '@api/features/invoices/getAccountingItemsForApproval';
import type {
	SubmitHireInvoiceRequest,
	SubmitHireInvoiceResponse,
} from '@api/features/invoices/hire-invoices/submitHireInvoice';
import type {
	ApproveHireInvoiceRequest,
	ApproveHireInvoiceResponse,
} from '@api/features/invoices/hire-invoices/approveHireInvoice';
import type {
	RejectHireInvoiceRequest,
	RejectHireInvoiceResponse,
} from '@api/features/invoices/hire-invoices/rejectHireInvoice';
import type {
	UnsubmitHireInvoiceRequest,
	UnsubmitHireInvoiceResponse,
} from '@api/features/invoices/hire-invoices/unsubmitHireInvoice';
import type {
	CreateRevenueItemRequest,
	CreateRevenueItemResponse,
} from '@api/features/invoices/createRevenueItem';
import type {
	UpdateRevenueItemRequest,
	UpdateRevenueItemResponse,
} from '@api/features/invoices/updateRevenueItem';
import type {
	UpdateEstimateGroupRequest,
	UpdateEstimateGroupResponse,
} from '@api/features/estimates/updateEstimateGroup';
import type {
	GetAccountingItemsRequest,
	GetAccountingItemsResponse,
} from '@api/features/financials/getAccountingItems';
import type {
	ExportAccountingItemsRequest,
	ExportAccountingItemsResponse,
} from '@api/features/financials/exportAccountingItems';
import type {
	UpdateVesselRequest,
	UpdateVesselResponse,
	UpdateVesselAttributes,
} from '@api/features/vessels/updateVessel';
import type { GetUsersResponse } from '@api/features/organizations/getUsers';
import type {
	UpdateUserDepartmentsRequest,
	UpdateUserDepartmentsResponse,
} from '@api/features/organizations/updateUserDepartments';
import type {
	InviteTeamMemberRequest,
	InviteTeamMemberResponse,
} from '@api/features/organizations/inviteTeamMember';
import type {
	RemoveTeamMemberRequest,
	RemoveTeamMemberResponse,
} from '@api/features/organizations/removeTeamMember';
import type {
	CreateBrokerInvoiceRequest,
	CreateBrokerInvoiceResponse,
} from '@api/features/brokers/invoices/createBrokerInvoice';
import type {
	SubmitBrokerInvoiceRequest,
	SubmitBrokerInvoiceResponse,
} from '@api/features/brokers/invoices/submitBrokerInvoice';
import type {
	DeleteBrokerInvoiceRequest,
	DeleteBrokerInvoiceResponse,
} from '@api/features/brokers/invoices/deleteBrokerInvoice';
import type {
	UnsubmitBrokerInvoiceRequest,
	UnsubmitBrokerInvoiceResponse,
} from '@api/features/brokers/invoices/unsubmitBrokerInvoice';
import type {
	UpdateBrokerInvoiceRequest,
	UpdateBrokerInvoiceResponse,
} from '@api/features/brokers/invoices/updateBrokerInvoice';
import type {
	ApproveBrokerInvoiceRequest,
	ApproveBrokerInvoiceResponse,
} from '@api/features/brokers/invoices/approveBrokerInvoice';
import type {
	RejectBrokerInvoiceRequest,
	RejectBrokerInvoiceResponse,
} from '@api/features/brokers/invoices/rejectBrokerInvoice';
import type {
	UpdateVoyageExpenseRequest,
	UpdateVoyageExpenseResponse,
} from '@api/features/invoices/updateVoyageExpense';
import type {
	GetAccrualItemsRequest,
	GetAccrualItemsResponse,
	TransformedAccrualItem,
} from '@api/features/financials/getAccrualItems';
import type {
	DeleteVoyageExpenseRequest,
	DeleteVoyageExpenseResponse,
} from '@api/features/invoices/deleteVoyageExpense';
import type {
	GetClosedPeriodsRequest,
	GetClosedPeriodsResponse,
} from '@api/features/financials/getClosedPeriods';
import type {
	SendAdminInvitationRequest,
	SendAdminInvitationsResponse,
} from '@api/features/admin/sendAdminInvitation';
import type {
	SendUserInvitationsRequest,
	SendUserInvitationsResponse,
} from '@api/features/admin/sendUserInvitations';
import type {
	SendWelcomeEmailRequest,
	SendWelcomeEmailResponse,
} from '@api/features/admin/sendWelcomeEmail';
import type {
	UpdateOrganizationUserRequest,
	UpdateOrganizationUserResponse,
} from '@api/features/admin/updateOrganizationUser';
import type {
	ToggleUserDisabledRequest,
	ToggleUserDisabledResponse,
} from '@api/features/admin/toggleUserDisabled';
import type {
	SendUserInvitationRequest,
	SendUserInvitationResponse,
} from '@api/features/admin/sendUserInvitation';
import type {
	GetUserInvitationLinkRequest,
	GetUserInvitationLinkResponse,
} from '@api/features/admin/getUserInvitationLink';
import type {
	SetupUserPasswordLoginRequest,
	SetupUserPasswordLoginResponse,
} from '@api/features/admin/setupUserPasswordLogin';
import type {
	Attributes,
	UpdateAttachmentRequest,
	UpdateAttachmentResponse,
} from '@api/features/attachments/updateAttachment';
import type {
	AddAttachmentRequest,
	AddAttachmentResponse,
} from '@api/features/attachments/addAttachment';
import type { ActivateOrganizationRequest } from '@api/features/auth/activateOrganization';
import type {
	GetActivationDetailsRequest,
	GetActivationDetailsResponse,
} from '@api/features/auth/getActivationDetails';
import type {
	UpdateBrokerAttributes,
	UpdateBrokerRequest,
	UpdateBrokerResponse,
} from '@api/features/brokers/updateBroker';
import type {
	GetBrokerDetailsRequest,
	GetBrokerDetailsResponse,
} from '@api/features/brokers/getBrokerDetails';
import type {
	DeleteBrokerRequest,
	DeleteBrokerResponse,
} from '@api/features/brokers/deleteBroker';
import type {
	CreateCounterpartyRequest,
	CreateCounterpartyResponse,
} from '@api/features/counterparties/createCounterparty';
import type {
	GetCounterpartyDetailsRequest,
	GetCounterpartyDetailsResponse,
} from '@api/features/counterparties/getCounterpartyDetails';
import type {
	GetReceivablesAgingDataRequest,
	GetReceivablesAgingDataResponse,
} from '@api/features/statistics/getReceivablesAgingData';
import type {
	GetPayablesAgingDataRequest,
	GetPayablesAgingDataResponse,
} from '@api/features/statistics/getPayablesAgingData';
import type {
	DeleteCounterpartyRequest,
	DeleteCounterpartyResponse,
} from '@api/features/counterparties/deleteCounterparty';
import type {
	UpdateAccrualItemRequest,
	UpdateAccrualItemResponse,
} from '@api/features/financials/updateAccrualItem';
import type {
	GetVideoGuidesRequest,
	GetVideoGuidesResponse,
} from '@api/features/admin/getVideoGuides';
import type {
	CreateVideoGuideRequest,
	CreateVideoGuideResponse,
} from '@api/features/admin/createVideoGuide';
import type {
	UpdateVideoGuideRequest,
	UpdateVideoGuideResponse,
} from '@api/features/admin/updateVideoGuide';
import type {
	DeleteVideoGuideRequest,
	DeleteVideoGuideResponse,
} from '@api/features/admin/deleteVideoGuide';
import type { UpdateVideoGuideOrderRequest } from '@api/features/admin/updateVideoGuideOrder';
import type { VideoGuideProps } from '@api/models/video-guide';
import type {
	GetVoyageDurationRequest,
	GetVoyageDurationResponse,
} from '@api/features/voyages/getVoyageDurations';
import type {
	ReportGroupAttributes,
	UpdateReportGroupsRequest,
	UpdateReportGroupsResponse,
} from '@api/features/organizations/updateReportGroups';
import type {
	UpdateOffHireTypesAttributes,
	UpdateOffHireTypesRequest,
	UpdateOffHireTypesResponse,
} from '@api/features/organizations/updateOffHireTypes';
import type {
	UpdatePortCostCategoriesRequest,
	UpdatePortCostCategoriesResponse,
} from '@api/features/organizations/updatePortCostCategories';
import type {
	GetReportGroupsRequest,
	GetReportGroupsResponse,
} from '@api/features/organizations/getReportGroups';
import type {
	CreateDisbursementAccountRequest,
	CreateDisbursementAccountResponse,
} from '@api/features/disbursement-accounts/createDisbursementAccount';
import type {
	UpdateDisbursementAccountRequest,
	UpdateDisbursementAccountResponse,
} from '@api/features/disbursement-accounts/updateDisbursementAccount';
import type {
	DeleteDisbursementAccountRequest,
	DeleteDisbursementAccountResponse,
} from '@api/features/disbursement-accounts/deleteDisbursementAccount';
import type {
	GetDisbursementAccountsRequest,
	GetDisbursementAccountsResponse,
} from '@api/features/disbursement-accounts/getDisbursementAccounts';
import type {
	GetDisbursementAccountRequest,
	GetDisbursementAccountResponse,
} from '@api/features/disbursement-accounts/getDisbursementAccount';
import type {
	GetHistoricalDisbursementAccountsRequest,
	GetHistoricalDisbursementAccountsResponse,
} from '@api/features/disbursement-accounts/getHistoricalDisbursementAccounts';
import type {
	GetVoyageItineraryRequest,
	GetVoyageItineraryResponse,
} from '@api/features/voyages/getVoyageItinerary';
import type {
	GetPreviousVoyageBunkersRequest,
	GetPreviousVoyageBunkersResponse,
} from '@api/features/voyages/getPreviousVoyageBunkers';
import type {
	GetVoyageInvoicesRequest,
	GetVoyageInvoicesResponse,
} from '@api/features/invoices/getVoyageInvoices';
import type {
	GetSeaPassageConsumptionRequest,
	GetSeaPassageConsumptionResponse,
} from '@api/features/performance/getSeaPassageConsumption';
import type {
	UpdateVesselConsumptionEntryRequest,
	UpdateVesselConsumptionEntryResponse,
} from '@api/features/vessels/updateVesselConsumptionEntries';
import type {
	CreateVesselOtherConsumptionSetRequest,
	CreateVesselOtherConsumptionSetResponse,
} from '@api/features/vessels/createVesselOtherConsumptionSet';
import type {
	UpdateVesselOtherConsumptionEntryRequest,
	UpdateVesselOtherConsumptionEntryResponse,
} from '@api/features/vessels/updateVesselOtherConsumptionEntries';
import type {
	CreateVesselConsumptionSetRequest,
	CreateVesselConsumptionSetResponse,
} from '@api/features/vessels/createVesselConsumptionSet';
import type {
	UpdateVesselConsumptionSetRequest,
	UpdateVesselConsumptionSetResponse,
} from '@api/features/vessels/updateVesselConsumptionSet';
import type {
	CreateBbItemRequest,
	CreateBbItemResponse,
} from '@api/features/fixtures/createBbItem';
import type {
	DeleteVesselOtherPerformanceEntryRequest,
	DeleteVesselOtherPerformanceEntryResponse,
} from '@api/features/vessels/deleteVesselOtherPerformanceEntry';
import type {
	DeleteVesselConsumptionSetRequest,
	DeleteVesselConsumptionSetResponse,
} from '@api/features/vessels/deleteVesselConsumptionSet';
import type {
	GetOutstandingsDataRequest,
	GetOutstandingsDataResponse,
} from '@api/features/statistics/getOutstandingsData';
import type {
	GetFullFleetTimelineDataRequest,
	GetFullFleetTimelineDataResponse,
} from '@api/features/statistics/getFullFleetTimelineData';
import type { CustomFieldValueTypes } from '@api/models/custom-field';
import type {
	CreateCustomFieldRequest,
	CreateCustomFieldResponse,
} from '@api/features/misc/createCustomField';
import type {
	GetCustomFieldsRequest,
	GetCustomFieldsResponse,
} from '@api/features/misc/getCustomFields';
import type {
	DeleteCustomFieldRequest,
	DeleteCustomFieldResponse,
} from '@api/features/misc/deleteCustomField';
import type {
	UpdateCustomFieldRequest,
	UpdateCustomFieldResponse,
} from '@api/features/misc/updateCustomField';
import type {
	GetCompanyAddressesRequest,
	GetCompanyAddressesResponse,
} from '@api/features/organizations/company-address/getCompanyAddresses';
import type {
	CreateCompanyAddressAttributes,
	CreateCompanyAddressRequest,
	CreateCompanyAddressResponse,
} from '@api/features/organizations/company-address/createCompanyAddress';
import type {
	UpdateCompanyAddressAttributes,
	UpdateCompanyAddressRequest,
	UpdateCompanyAddressResponse,
} from '@api/features/organizations/company-address/updateCompanyAddress';
import type { UpdatePortRotationRequest } from '@api/features/estimates/updatePortRotation';
import type {
	MarkFixtureAsUnfixedRequest,
	MarkFixtureAsUnfixedResponse,
} from '@api/features/fixtures/markFixtureAsUnfixed';
import type { BbFixtureProps } from '@api/models/bb-fixture';
import type {
	UpdateBbItemRequest,
	UpdateBbItemResponse,
} from '@api/features/fixtures/updateBbItem';
import type {
	DeleteBbItemRequest,
	DeleteBbItemResponse,
} from '@api/features/fixtures/deleteBbItem';
import type {
	RenameIdentifierRequest,
	RenameIdentifierResponse,
} from '@api/features/fixtures/renameIdentifier';
import type {
	GetProjectRequest,
	GetProjectResponse,
} from '@api/features/voyages/project/getProject';
import type {
	GetBunkerStemsRequest,
	GetBunkerStemsResponse,
} from '@api/features/voyages/bunker-stems/getBunkerStems';
import type { CreateBunkerStemRequest } from '@api/features/voyages/bunker-stems/createBunkerStem';
import type { UpdateBunkerStemRequest } from '@api/features/voyages/bunker-stems/updateBunkerStem';
import type {
	GetFleetAnalyticsRequest,
	GetFleetAnalyticsResponse,
} from '@api/features/statistics/getFleetAnalytics';
import type {
	DeleteCacheEntryRequest,
	DeleteCacheEntryResponse,
} from '@api/features/misc/deleteCacheEntry';
import type {
	GetEuasRequest,
	GetEuasResponse,
} from '@api/features/voyages/euas/getEuas';
import type {
	CreateEuaRequest,
	CreateEuaResponse,
} from '@api/features/voyages/euas/createEua';
import type {
	UpdateEuaRequest,
	UpdateEuaResponse,
} from '@api/features/voyages/euas/updateEua';
import type {
	DeleteEuaRequest,
	DeleteEuaResponse,
} from '@api/features/voyages/euas/deleteEua';
import type {
	GetOpenPositionsDataRequest,
	GetOpenPositionsDataResponse,
} from '@api/features/statistics/getOperationsData';
import type {
	GetPayablesDataRequest,
	GetPayablesDataResponse,
} from '@api/features/statistics/getPayablesData';
import type { UpdateCargoTypeRequest } from
	'@api/features/organizations/cargo-types/updateCargoType';
import type { UpdateOrgCanalTransitDefaultsRequest } from
	'@api/features/organizations/canal-defaults/updateOrgCanalTransitDefaults';
import type { CreateCargoTypeRequest } from
	'@api/features/organizations/cargo-types/createCargoType';
import type {
	GetPortActivitiesRequest,
	GetPortActivitiesResponse,
} from '@api/features/port-activities/getPortActivities';
import type {
	CreatePortActivityRequest,
	CreatePortActivityResponse,
} from '@api/features/port-activities/createPortActivity';
import type {
	UpdatePortActivityRequest,
	UpdatePortActivityResponse,
} from '@api/features/port-activities/updatePortActivity';
import type {
	ReorderPortActivitiesRequest,
	ReorderPortActivitiesResponse,
} from '@api/features/port-activities/reorderPortActivities';
import type {
	GetFixingCompaniesRequest,
	GetFixingCompaniesResponse,
} from '@api/features/fixing-companies/getFixingCompanies';
import type {
	CreateFixingCompanyRequest,
	CreateFixingCompanyResponse,
} from '@api/features/fixing-companies/createFixingCompany';
import type {
	UpdateFixingCompanyRequest,
	UpdateFixingCompanyResponse,
} from '@api/features/fixing-companies/updateFixingCompany';
import type {
	GetExclusionItemsRequest,
	GetExclusionItemsResponse,
} from '@api/features/exclusions/getExclusionItems';
import type {
	CreateExclusionItemRequest,
	CreateExclusionItemResponse,
} from '@api/features/exclusions/createExclusionItem';
import type {
	UpdateExclusionItemRequest,
	UpdateExclusionItemResponse,
} from '@api/features/exclusions/updateExclusionItem';
import type {
	DeleteExclusionItemRequest,
	DeleteExclusionItemResponse,
} from '@api/features/exclusions/deleteExclusionItem';
import type {
	DeletePortActivityRequest,
	DeletePortActivityResponse,
} from '@api/features/port-activities/deletePortActivity';
import type {
	GenerateLaytimePdfRequest,
	GenerateLaytimePdfResponse,
} from '@api/features/laytime/getLaytimePdf';
import type {
	GenerateVcPdfRequest,
	GenerateVcPdfResponse,
} from '@api/features/voyages/getVcPdf';
import type {
	SplitCargoRequest,
	SplitCargoResponse,
	SplitRow,
} from '@api/features/cargos/splitCargo';
import type {
	UndoSplitCargoRequest,
	UndoSplitCargoResponse,
} from '@api/features/cargos/undoSplitCargo';
import type { BunkerRecord } from '@client/screens/fleet/VoyageDetailsScreen/tabs/components/RobEntryDrawer';
import { RootPages } from '@client/utils/links';
import type { UserInvite } from '@client/screens/admin/OrganizationDetailsScreen/components/InviteUsersDrawer';
import type { PartialPortRotationEntry } from '@client/screens/estimates/details/context/hooks/usePortRotationHandlers';
import type { CommencementRobBunker } from '@client/screens/fleet/VoyageDetailsScreen/components/ItineraryTab/utils/bunkerTabHelpers';
import { NullableBunkerRecord } from '@client/screens/estimates/details/helpers/types';
import {
	sendRequest,
	sendRequestWithFiles,
} from './sendRequest';

export const getOrganizationDetails = (organizationId: number) => sendRequest<
	GetOrganizationDetailsRequest,
	GetOrganizationDetailsResponse
>(
	`/admin/organizations/${organizationId}`,
	'get',
	{ id: organizationId },
);

export const getOffHireData = (
	startDate: Moment | undefined,
	endDate: Moment | undefined
) => sendRequest<GetOffHireDataRequest, GetOffHireDataResponse>(
	'/statistics/off-hire',
	'post',
	{ startDate, endDate },
);

export const exportOffHireData = (
	startDate: Moment | undefined,
	endDate: Moment | undefined
) => sendRequest<ExportOffHireDataRequest, ExportOffHireDataResponse>(
	'/statistics/off-hire/export',
	'post',
	{ startDate, endDate },
	{ responseType: 'blob', responseEncoding: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
);

export const exportPositionList = (
	exportType: 'activeContracts' | 'itineraries',
) => sendRequest<{ exportType: 'activeContracts' | 'itineraries' }, Blob>(
	'/statistics/position-list/export',
	'post',
	{ exportType },
	{ responseType: 'blob', responseEncoding: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
);

export const getVersionInfo = () => sendRequest<undefined,
	{
		LATEST_TAG: string;
		LATEST_COMMIT: string;
		NODE_ENV: string;
	}>('/version');

export const getVessels = ({
	vesselIndexSearchQuery,
	includeMarketVessels,
	includeVesselIndex,
}: GetVesselsRequest = {}) => sendRequest<GetVesselsRequest, GetVesselsResponse>(
	'/vessels',
	'get',
	{ vesselIndexSearchQuery, includeMarketVessels, includeVesselIndex },
);

export const createVessel = (
	name: string,
	ownershipType: Values<typeof VesselOwnershipTypes>,
	type?: Values<typeof VesselTypes> | null,
	size?: string | null,
) => sendRequest<CreateVesselRequest, CreateVesselResponse>(
	'/vessels',
	'post',
	{ name, ownershipType, type, size },
);

export const updateVessel = (
	vesselId: number,
	attributes: UpdateVesselAttributes,
	estimateId?: number,
) => sendRequest<
	UpdateVesselRequest,
	UpdateVesselResponse
>(
	`/vessels/${vesselId}`,
	'patch',
	{ id: vesselId, attributes, estimateId },
);

export const getVesselDetails = (vesselId: number) => sendRequest<GetVesselDetailsRequest, GetVesselDetailsResponse>(
	`/vessels/${vesselId}`,
	'get',
	{ id: vesselId },
);

export const calculateRequiredArrivalSpeed = (
	attributes: CalculateRequiredArrivalSpeedRequest,
) => sendRequest<CalculateRequiredArrivalSpeedRequest, CalculateRequiredArrivalSpeedResponse>(
	'/vessels/calculate-required-speed',
	'post',
	attributes,
);

export const updatePeriodScheduleItem = (
	id: number,
	attributes: UpdatePeriodScheduleItemRequest['attributes'],
) => sendRequest<
	UpdatePeriodScheduleItemRequest,
	UpdatePeriodScheduleItemResponse
>(
	`/fixtures/rate-schedule/${id}`,
	'patch',
	{
		id,
		attributes,
	},
);

export const createVesselBunker = (
	vesselId: number,
	values: Omit<CreateVesselBunkerRequest, 'vesselId'>,
) => sendRequest<CreateVesselBunkerRequest, CreateVesselBunkerResponse>(
	`/vessels/${vesselId}/bunkers`,
	'post',
	{ ...values, vesselId },
);

export const updateVesselBunker = (
	vesselId: number,
	vesselBunkerId: number,
	attributes: UpdateVesselBunkerRequest['attributes'],
) => sendRequest<UpdateVesselBunkerRequest, UpdateVesselBunkerResponse>(
	`/vessels/${vesselId}/bunkers/${vesselBunkerId}`,
	'patch',
	{ vesselId, vesselBunkerId, attributes },
);

export const deleteVesselBunker = (
	vesselId: number,
	vesselBunkerId: number,
) => sendRequest<DeleteVesselBunkerRequest, DeleteVesselBunkerResponse>(
	`/vessels/${vesselId}/bunkers/${vesselBunkerId}`,
	'delete',
	{ vesselBunkerId, vesselId },
);

export const deleteItineraryEntry = (
	vesselId: number,
	portCallId: number,
) => sendRequest<DeleteItineraryEntryRequest, DeleteItineraryEntryResponse>(
	`/ops/itinerary/${vesselId}/${portCallId}`,
	'delete',
	{ portCallId, vesselId },
);

export const updatePortCall = (
	vesselId: number,
	portCallId: number,
	attributes: UpdatePortCallRequest['attributes'],
	savingIndicator: boolean = true,
) => sendRequest<UpdatePortCallRequest, UpdatePortCallResponse>(
	`/ops/itinerary/${vesselId}/pc/${portCallId}`,
	'patch',
	{ portCallId, vesselId, attributes },
	{},
	{ savingIndicator },
);

export const getDaDeskPortCallInfo = (
	vesselId: number,
	portCallId: number,
) => sendRequest<GetDaDeskPortCallInfoRequest, GetDaDeskPortCallInfoResponse>(
	`/ops/itinerary/${vesselId}/pc/${portCallId}/da-desk`,
	'get',
	{ vesselId, portCallId },
);

export const deletePortCallAction = (
	vesselId: number,
	portCallId: number,
	actionId: number,
) => sendRequest<DeletePortCallActionRequest, DeletePortCallActionResponse>(
	`/ops/itinerary/${vesselId}/pc/${portCallId}/action/${actionId}`,
	'delete',
	{ actionId, portCallId, vesselId },
);

export const deletePortCallActivity = (
	vesselId: number,
	portCallId: number,
	activityId: number,
) => sendRequest<DeletePortCallActivityRequest, DeletePortCallActivityResponse>(
	`/ops/itinerary/${vesselId}/pc/${portCallId}/activity/${activityId}`,
	'delete',
	{ activityId, portCallId, vesselId },
);

export const updatePortCallAction = (
	vesselId: number,
	portCallId: number,
	actionId: number,
	event: string,
	date: Moment,
	cargoId?: number,
) => sendRequest<UpdatePortCallActionRequest, UpdatePortCallActionResponse>(
	`/ops/itinerary/${vesselId}/pc/${portCallId}/action/${actionId}`,
	'patch',
	{ vesselId, portCallId, actionId, event, date, cargoId },
	{},
	{ savingIndicator: true },
);

export const updatePortCallActivity = (
	vesselId: number,
	portCallId: number,
	activityId: number,
	event: string,
	date: Moment,
	portId: number,
	cargoId?: number,
) => sendRequest<UpdatePortCallActivityRequest, UpdatePortCallActivityResponse>(
	`/ops/itinerary/${vesselId}/pc/${portCallId}/activity/${activityId}`,
	'patch',
	{ vesselId, portCallId, activityId, event, date, cargoId, portId },
	{},
	{ savingIndicator: true },
);

export const createPortCallActivity = (
	vesselId: number,
	portCallId: number,
	event: string,
	date: Moment,
	portId: number,
	cargoId?: number,
) => sendRequest<CreatePortCallActivityRequest, CreatePortCallActivityResponse>(
	`/ops/itinerary/${vesselId}/pc/${portCallId}/activity`,
	'post',
	{ vesselId, portCallId, event, date, cargoId, portId },
	{},
	{ savingIndicator: true },
);

export const updateSeaPassage = (
	vesselId: number,
	seaPassageId: number,
	attributes: Partial<UpdateSeaPassageRequest['attributes']>,
	savingIndicator: boolean = false,
) => sendRequest<UpdateSeaPassageRequest, UpdateSeaPassageResponse>(
	`/ops/itinerary/${vesselId}/sp/${seaPassageId}`,
	'patch',
	{ seaPassageId, vesselId, attributes },
	{},
	{ savingIndicator },
);

export const getSeaPassageConsumption = (
	seaPassageId: number,
) => sendRequest<GetSeaPassageConsumptionRequest, GetSeaPassageConsumptionResponse>(
	`/performance/sp-performance/${seaPassageId}`,
	'get',
	{
		seaPassageId,
	},
);

export const createItineraryEntry = ({
	vesselId,
	voyageId,
	portId,
	estimatedDepartureDate,
	actualDepartureDate,
	robs,
	savingIndicator = false,
}: {
	vesselId: number;
	portId: number;
	estimatedDepartureDate?: Moment;
	savingIndicator: boolean;
	actualDepartureDate?: Moment;
	robs: CreateItineraryEntryRequest['robs'];
	voyageId: number;
}) => sendRequest<
	CreateItineraryEntryRequest,
	CreateItineraryEntryResponse
>(
	`/ops/itinerary/${vesselId}`,
	'post',
	{
		vesselId,
		voyageId,
		portId,
		estimatedDepartureDate,
		actualDepartureDate,
		robs,
	},
	{},
	{ savingIndicator },
);

export const createPeriodScheduleItem = ({
	exercised,
	description,
	hireType,
	periodType,
	maximumDurationExact,
	durationMaximumUnit,
	durationMaximumValue,
	minimumDurationExact,
	durationMinimumUnit,
	useExactDate,
	durationMinimumValue,
	tcTermsId,
	hireRate,
}: CreatePeriodScheduleItemRequest) => sendRequest<CreatePeriodScheduleItemRequest,
	CreatePeriodScheduleItemResponse>(
	'/fixtures/rate-schedule',
	'post',
	{
		exercised,
		description,
		hireType,
		periodType,
		maximumDurationExact,
		durationMaximumUnit,
		durationMaximumValue,
		minimumDurationExact,
		durationMinimumUnit,
		durationMinimumValue,
		hireRate,
		tcTermsId,
		useExactDate,
	},
);

export const deletePeriodScheduleItem = (
	id: number,
) => sendRequest<DeletePeriodScheduleItemRequest, DeletePeriodScheduleItemResponse>(
	`/fixtures/rate-schedule/${id}`,
	'delete',
	{ id },
);

export const getSuppliers = () => sendRequest<GetSuppliersRequest, GetSuppliersResponse>(
	'/suppliers',
);

export const createSupplier = (
	name: string,
) => sendRequest<CreateSupplierRequest, CreateSupplierResponse>(
	'/suppliers',
	'post',
	{ name },
);

export const updateSupplier = (
	id: number,
	attributes: UpdateSupplierRequest['attributes'],
) => sendRequest<UpdateSupplierRequest, UpdateSupplierResponse>(
	`/suppliers/${id}`,
	'patch',
	{
		id,
		attributes,
	},
);

export const updateCounterparty = (
	id: number,
	attributes: UpdateCounterpartyRequest['attributes'],
) => sendRequest<UpdateCounterpartyRequest, UpdateCounterpartyResponse>(
	`/counterparties/${id}`,
	'patch',
	{
		id,
		attributes,
	},
);

export const deleteSupplier = (
	id: number,
) => sendRequest<DeleteSupplierRequest, DeleteSupplierResponse>(
	`/suppliers/${id}`,
	'delete',
	{ id },
);

export const getVoyages = (
	params?: GetVoyagesRequest
) => sendRequest<
	GetVoyagesRequest,
	GetVoyagesResponse
>(
	'/voyages',
	'get',
	// @ts-ignore
	params
);

export const getFullFleetExport = () => sendRequest<
	void,
	void
>(
	'/statistics/full-fleet-export',
	'post',
);

export const getAccountingItems = (
	options: GetAccountingItemsRequest['options'],
) => sendRequest<
	GetAccountingItemsRequest,
	GetAccountingItemsResponse
>('/financials/accounting-items', 'get', { options });

export const exportAccountingItems = () => sendRequest<
	ExportAccountingItemsRequest,
	ExportAccountingItemsResponse
>(
	'/financials/accounting-items/export',
	'post',
	undefined,
	{ responseType: 'blob', responseEncoding: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
);

export type PostAccountingItemsFailure = { customId: string; reasons: string[] };
export type PostAccountingItemsResponse = { successes: string[]; failures: PostAccountingItemsFailure[] };

export const postAccountingItems = async (
	request: ApiPostAccountingItemsRequest,
): Promise<PostAccountingItemsResponse> => {
	return await sendRequest<ApiPostAccountingItemsRequest, PostAccountingItemsResponse>(
		'/financials/accounting-items',
		'post',
		request,
	);
};

export const archiveAccountingItem = (customId: string) => sendRequest<
	ArchiveAccountingItemRequest,
	ArchiveAccountingItemResponse
>(
	'/financials/accounting-items/archive',
	'post',
	{ customId },
);

export const closeAccountingItems = (
	items: TransformedAccrualItem[],
	closingPeriod: Moment,
	closingPeriodFormat: Values<typeof AccrualItemClosingPeriodFormats>,
	preview?: boolean,
) => sendRequest<
	CloseAccountingItemsRequest,
	CloseAccountingItemsResponse
>(
	'/financials/accounting-items/close',
	'post',
	{ items, closingPeriod, closingPeriodFormat, preview },
);

export const updateVoyage = (voyageId: number, attributes: UpdateVoyageAttributes) => sendRequest<
	UpdateVoyageRequest,
	UpdateVoyageResponse
>(
	`/voyages/${voyageId}`,
	'patch',
	{ id: voyageId, attributes },
	{},
	{ savingIndicator: true }
);

export const syncVoyageToTheShip = (voyageId: number) => sendRequest<
	SyncToTheShipRequest,
	SyncToTheShipResponse
>(
	`/voyages/${voyageId}/sync-ts`,
	'patch',
	{ voyageId },
);

export const fetchFromTs = (voyageId: number) => sendRequest<
	FetchFromTsRequest,
	FetchFromTsResponse
>(
	`/voyages/${voyageId}/fetch-ts`,
	'get',
	{ voyageId },
);

export const updateOffHirePeriod = (
	voyageId: number,
	offHireId: number,
	attributes: UpdateOffHirePeriodAttributes,
	attachments: File[],
) => sendRequestWithFiles<
	UpdateOffHirePeriodRequest,
	UpdateOffHirePeriodResponse
>(
	`/voyages/${voyageId}/off-hire/${offHireId}`,
	'patch',
	{ voyageId, id: offHireId, attributes },
	attachments,
);

export const allocateVesselOffHirePeriod = (
	offHireId: number,
	vesselId: number,
	voyageId: number,
	hirePerDay: number,
	percentageForOwnersAccount: number,
	note: string,
	invoiceNote: string,
	bunkers: {
		id: number;
		quantity: number;
		pricePerTon: number;
		currency: Values<typeof Currencies>;
		exchangeRate: number;
		fuelGrade: Values<typeof FuelTypes>;
	}[],
) => sendRequest<
	AllocateVesselOffHirePeriodRequest,
	AllocateVesselOffHirePeriodResponse
>(
	`/vessels/${vesselId}/off-hire/allocate/${offHireId}`,
	'post',
	{ voyageId, bunkers, vesselId, id: offHireId, hirePerDay, percentageForOwnersAccount, note, invoiceNote },
);

export const unAllocateVesselOffHirePeriod = (
	offHireId: number,
	vesselId: number,
	voyageId: number,
) => sendRequest<
	UnAllocateVesselOffHirePeriodRequest,
	UnAllocateVesselOffHirePeriodResponse
>(
	`/vessels/${vesselId}/off-hire/un-allocate/${offHireId}`,
	'post',
	{ voyageId, vesselId, id: offHireId },
);

export const getVoyageDetails = (voyageId: number) => sendRequest<
	GetVoyageDetailsRequest,
	GetVoyageDetailsResponse
>(`/voyages/${voyageId}`, 'get', { id: voyageId });

export const getVoyageCompletionType = (voyageId: number) => sendRequest<
	GetVoyageCompletionTypeRequest,
	GetVoyageCompletionTypeResponse
>(`/voyages/${voyageId}/completion-type`, 'get', { id: voyageId });

export const getCargoDetails = (cargoId: number) => sendRequest<
	GetCargoDetailsRequest,
	GetCargoDetailsResponse
>(`/cargos/${cargoId}`, 'get', { id: cargoId });

export const copyCargo = (
	id: number,
	isGhost?: boolean,
) => sendRequest<CopyCargoRequest, CopyCargoResponse>(
	`/cargos/copy/cargo/${id}`, 'post', { id, isGhost },
);

export const createVoyageBunker = (
	voyageId: number,
	values: Omit<CreateVoyageBunkersRequest, 'voyageId'>,
) => sendRequest<CreateVoyageBunkersRequest, CreateVoyageBunkersResponse>(
	`/voyages/${voyageId}/bunkers`,
	'post',
	{
		...values,
		voyageId,
	},
);

export const updateVoyageBunker = (
	voyageId: number,
	voyageBunkerId: number,
	attributes: UpdateVoyageBunkersRequest['attributes'],
) => sendRequest<UpdateVoyageBunkersRequest, UpdateVoyageBunkersResponse>(
	`/voyages/${voyageId}/bunkers/${voyageBunkerId}`,
	'patch',
	{
		id: voyageBunkerId,
		voyageId,
		attributes,
	},
);

export const updateHireInvoice = (
	voyageId: number,
	hireInvoiceId: number,
	items: HireInvoiceItem[],
	generateCreditNote: boolean,
) => sendRequest<UpdateHireInvoiceRequest, UpdateHireInvoiceResponse>(
	`/voyages/${voyageId}/cumulative`,
	'post',
	{
		voyageId,
		hireInvoiceId,
		items,
		generateCreditNote,
	},
);

export const updateVoyageBrokerPayment = (
	voyageId: number,
	brokerId: number,
	itemId: number,
	note: string | null,
) => sendRequest(
	`/voyages/${voyageId}/broker-payment`,
	'patch',
	{ itemId, brokerId, note },
	{},
	{ savingIndicator: true },
);

export const deleteVoyageBunker = (
	voyageId: number,
	voyageBunkerId: number,
) => sendRequest<DeleteVoyageBunkersRequest, DeleteVoyageBunkersResponse>(
	`/voyages/${voyageId}/bunkers/${voyageBunkerId}`,
	'delete',
	{
		id: voyageBunkerId,
		voyageId,
	},
);

export const createRevenueItem = ({
	voyageId,
	cargoId,
	itemDescription,
	amount,
	currency,
	exchangeRate,
	note,
	chartererId,
	estimated,
	subjectToCommissions,
}: {
	voyageId: number;
	cargoId: number;
	itemDescription: string;
	amount: number;
	currency: Values<typeof Currencies>;
	exchangeRate: number;
	note?: string | null;
	chartererId: number;
	subjectToCommissions: boolean;
	estimated: boolean;
}) => sendRequest<CreateRevenueItemRequest, CreateRevenueItemResponse>(
	'/invoices/revenue-items',
	'post',
	{
		voyageId,
		cargoId,
		itemDescription,
		amount,
		currency,
		exchangeRate,
		note,
		chartererId,
		subjectToCommissions,
		estimated,
	},
);

export const updateRevenueItem = ({
	id,
	cargoId,
	itemDescription,
	amount,
	currency,
	exchangeRate,
	note,
	chartererId,
	subjectToCommissions,
	estimated,
}: {
	id: number;
	cargoId: number;
	itemDescription: string;
	amount: number;
	currency: Values<typeof Currencies>;
	exchangeRate: number;
	note?: string | null;
	chartererId: number;
	subjectToCommissions: boolean;
	estimated: boolean;
}) => sendRequest<UpdateRevenueItemRequest, UpdateRevenueItemResponse>(
	`/invoices/revenue-items/${id}`,
	'patch',
	{
		id,
		cargoId,
		itemDescription,
		amount,
		currency,
		exchangeRate,
		note,
		chartererId,
		subjectToCommissions,
		estimated,
	},
);

export const deleteRevenueItem = (id: number) => sendRequest(`/invoices/revenue-items/${id}`, 'delete', { id });

export const createVoyageExpenseReceivable = ({
	voyageExpenseId,
	voyageId,
	itemDescription,
	customInvoiceId,
	account,
	amount,
	supplier,
	chartererId,
	invoiceDate,
	currency,
	exchangeRate,
	note,
	attachments,
	estimated,
	quantity,
	unit,
	unitPrice,
	accountCodeOverride,
	isFuelEuPenalty,
	isEuEts,
	allocatedToId,
}: {
	voyageExpenseId?: number;
	voyageId: number;
	itemDescription: string;
	customInvoiceId?: string | null;
	account: Values<typeof AccountTypes>;
	amount: number;
	supplier?: number;
	chartererId?: number;
	invoiceDate: Moment;
	currency: Values<typeof Currencies>;
	exchangeRate: number;
	note?: string;
	estimated?: boolean;
	attachments?: File[];
	quantity?: number;
	unit?: string;
	unitPrice?: number;
	accountCodeOverride?: string | null;
	isFuelEuPenalty?: boolean;
	isEuEts?: boolean;
	allocatedToId?: number | null;
}) => {
	const data = new FormData();

	const parameters = {
		voyageExpenseId,
		voyageId,
		itemDescription,
		customInvoiceId,
		account,
		supplier,
		chartererId,
		invoiceDate,
		currency,
		exchangeRate,
		amount,
		note,
		estimated,
		quantity,
		unit,
		unitPrice,
		accountCodeOverride,
		isFuelEuPenalty,
		isEuEts,
		allocatedToId,
	};

	// Add all not-null values to form data
	Object.entries(parameters).forEach(([key, value]) => {
		if (value != null) {
			// @ts-ignore
			data.append(key, value);
		}
	});

	if (attachments != null) {
		attachments.forEach((a) => data.append('attachments', a));
	}

	return sendRequest(`/voyages/${voyageId}/expense-receivables`, 'post', data);
};

export const updateVoyageExpenseReceivable = async (
	voyageId: number,
	voyageExpenseReceivableId: number,
	attributes: UpdateVoyageExpenseReceivableRequest['attributes'],
	newAttachments: File[],
) => {
	await sendRequest<UpdateVoyageExpenseReceivableRequest, UpdateVoyageExpenseReceivableResponse>(
		`/voyages/${voyageId}/expense-receivables/${voyageExpenseReceivableId}`,
		'patch',
		{
			id: voyageExpenseReceivableId,
			attributes,
		},
	);

	// We can't upload new attachments and update data at the same time
	// So we do separate requests
	if (newAttachments != null && newAttachments.length > 0) {
		const data = new FormData();

		newAttachments.forEach((a) => data.append('newAttachments', a));

		await sendRequest(
			`/voyages/${voyageId}/expense-receivables/${voyageExpenseReceivableId}`,
			'patch',
			data,
		);
	}
};

export const deleteVoyageExpense = (
	id: number,
) => sendRequest<DeleteVoyageExpenseRequest, DeleteVoyageExpenseResponse>(
	`/invoices/voyage-expenses/${id}`,
	'delete',
	{ id },
);

export const deleteVoyageExpenseReceivable = (
	voyageId: number,
	voyageExpenseReceivableId: number,
) => sendRequest<DeleteVoyageExpenseReceivableRequest, DeleteVoyageExpenseReceivableResponse>(
	`/voyages/${voyageId}/expense-receivables/${voyageExpenseReceivableId}`,
	'delete',
	{
		voyageId,
		id: voyageExpenseReceivableId,
	},
);

export const approveExpense = (
	expenseId: number,
	note?: string,
) => sendRequest<ApproveExpenseRequest, ApproveExpenseResponse>(
	`/invoices/voyage-expenses/${expenseId}/approve`,
	'post',
	{
		id: expenseId,
		note,
	},
);

export const rejectExpense = (
	expenseId: number,
	reason: string,
) => sendRequest<RejectExpenseRequest, RejectExpenseResponse>(
	`/invoices/voyage-expenses/${expenseId}/reject`,
	'post',
	{
		id: expenseId,
		reason,
	},
);

export const submitDisbursementAccount = (
	disbursementAccountId: number,
) => sendRequest<SubmitDisbursementAccountRequest, SubmitDisbursementAccountResponse>(
	`/invoices/disbursement-accounts/${disbursementAccountId}/submit`,
	'post',
	{
		id: disbursementAccountId,
	},
);

export const unsubmitDisbursementAccount = (
	disbursementAccountId: number,
) => sendRequest<UnsubmitDisbursementAccountRequest, UnsubmitDisbursementAccountResponse>(
	`/invoices/disbursement-accounts/${disbursementAccountId}/unsubmit`,
	'post',
	{
		id: disbursementAccountId,
	},
);

export const approveDisbursementAccount = (
	disbursementAccountId: number,
	note?: string,
) => sendRequest<ApproveDisbursementAccountRequest, ApproveDisbursementAccountResponse>(
	`/invoices/disbursement-accounts/${disbursementAccountId}/approve`,
	'post',
	{
		id: disbursementAccountId,
		note,
	},
);

export const rejectDisbursementAccount = (
	disbursementAccountId: number,
	reason: string,
) => sendRequest<RejectDisbursementAccountRequest, RejectDisbursementAccountResponse>(
	`/invoices/disbursement-accounts/${disbursementAccountId}/reject`,
	'post',
	{
		id: disbursementAccountId,
		reason,
	},
);

export const deleteAttachment = (
	attachmentId: number,
) => sendRequest<DeleteAttachmentRequest, DeleteAttachmentResponse>(
	`/attachments/${attachmentId}`,
	'delete',
	{
		id: attachmentId,
	},
);

export const getAttachmentUrl = (
	attachmentId: number,
) => sendRequest<
	GetAttachmentUrlRequest,
	GetAttachmentUrlResponse
>(
	`/attachments/${attachmentId}/url`,
	'get',
	{ id: attachmentId },
);

export const getTcInAllocationItems = (
	voyageId: number,
	groupItems: boolean,
) => sendRequest<
	GetTcInAllocationItemsRequest,
	GetTcInAllocationItemsResponse
>(
	`/voyages/${voyageId}/allocation-items`,
	'get',
	{ voyageId, groupItems },
);

export const getVoyageHireItems = (
	voyageId: number,
) => sendRequest<
	GetVoyageHireItemsRequest,
	GetVoyageHireItemsResponse
>(
	`/voyages/${voyageId}/hire-periods`,
	'get',
	{ voyageId },
);

export const updateHireItem = (
	voyageId: number,
	id: number,
	attributes: UpdateHireItemRequest['attributes'],
) => sendRequest<
	UpdateHireItemRequest,
	UpdateHireItemResponse
>(
	`/voyages/${voyageId}/hire-periods/${id}`,
	'patch',
	{ voyageId, id, attributes },
);

export const getThirdPartyNoonReports = (
	vesselId: number,
	attributes: {
		from?: string; // iso date
		to?: string;
		portCallId?: string;
	},
) => sendRequest<GetThirdPartyReportsRequest, GetThirdPartyReportsResponse>(
	`/vessels/${vesselId}/third-party-reports/`,
	'get',
	{ vesselId, attributes },
);

export const getHireInvoiceDetails = (
	hireInvoiceId: number,
) => sendRequest<GetHireInvoiceDetailsRequest, GetHireInvoiceDetailsResponse>(
	`/invoices/hire-invoices/${hireInvoiceId}`,
	'get',
	{ id: hireInvoiceId },
);

export const updateHireInvoiceDetails = (
	hireInvoiceId: number,
	attributes: UpdateHireInvoiceDetailsRequest['attributes'],
) => sendRequest<UpdateHireInvoiceDetailsRequest, UpdateHireInvoiceDetailsResponse>(
	`/invoices/hire-invoices/${hireInvoiceId}`,
	'patch',
	{ id: hireInvoiceId, attributes },
);

export const updateTcInAllocationItem = (
	voyageId: number,
	itemId: number,
	itemType: string,
	percentage: number,
) => sendRequest<UpdateTcInAllocationItemRequest, UpdateTcInAllocationItemResponse>(
	`/voyages/${voyageId}/allocation-items`,
	'patch',
	{ itemId, itemType, voyageId, percentage },
);

export const createVoyagePayment = (
	voyageId: number,
	date: Moment,
	amount: number,
	parts: CreateVoyagePaymentRequest['parts'],
	bankCharge: number,
	type: VoyagePaymentTypes,
	currency: Values<typeof Currencies>,
	exchangeRate: number,
) => sendRequest<CreateVoyagePaymentRequest, CreateVoyagePaymentResponse>(
	`/voyages/${voyageId}/payments`,
	'post',
	{ voyageId, date, amount, parts, bankCharge, type, currency, exchangeRate },
);

export const createConnection = (
	belongsToId?: number | null,
	companyId?: string,
) => sendRequest<CreateConnectionRequest, CreateConnectionResponse>(
	'/accounting/connection',
	'post',
	{ belongsToId, companyId },
);

export const getAccountingCompanies = () => sendRequest<
	GetAccountingCompaniesRequest,
	GetAccountingCompaniesResponse
>('/accounting/companies');

export const disconnectAccountingSystem = (
	connectionId: number,
) => sendRequest<DisconnectAccountingSystemRequest, DisconnectAccountingSystemResponse>(
	`/accounting/connection/${connectionId}`,
	'delete',
	{ connectionId },
);

export const createMappingEntry = (
	type: 'fixtureCounterparty' | 'fixtureCounterpartyVendor' | 'supplier' | 'supplierCustomer' | 'accrual' | 'broker',
	cvIdentifier: string | number,
	code: string | undefined,
	connectionId?: number,
) => sendRequest<CreateMappingEntryRequest, CreateMappingEntryResponse>(
	'/accounting/mapping',
	'post',
	{ connectionId, type, cvIdentifier, code },
);

export const createVesselOffHirePeriod = (
	vesselId: number,
	startTime: Moment,
	endTime: Moment,
	type: string,
	preferDuration: boolean,
	selectedTimeFormat: TimeZoneFormat,
	useInvoiceTimeFormat: boolean,
	attachments: File[],
) => sendRequestWithFiles<CreateVesselOffHireRequest, CreateVesselOffHireResponse>(
	`/vessels/${vesselId}/off-hire`,
	'post',
	{ vesselId, startTime, preferDuration, endTime, type, selectedTimeFormat, useInvoiceTimeFormat },
	attachments,
);

export const updateVesselOffHirePeriod = (
	id: number,
	vesselId: number,
	attributes: UpdateVesselOffHirePeriodRequest['attributes'],
	attachments: File[],
) => sendRequestWithFiles<UpdateVesselOffHirePeriodRequest, UpdateVesselOffHirePeriodResponse>(
	`/vessels/${vesselId}/off-hire/${id}`,
	'patch',
	{ id, vesselId, attributes },
	attachments,
);

export const getConnectionAndMapping = (
	strategy: Values<typeof AccountingSystemStrategies>,
	id: number,
) => sendRequest<GetConnectionAndMappingRequest, GetConnectionAndMappingResponse>(
	`/accounting/connection/${strategy}/${id}`,
	'get',
	{ strategy, id },
);

export const getAccountingCustomers = (
	connectionId: number,
) => sendRequest<GetAccountingCustomersRequest, GetAccountingCustomersResponse>(
	`/accounting/connection/${connectionId}/customers`,
	'get',
	{ connectionId },
);

export const getCompanyAccounts = (
	connectionId: number,
) => sendRequest<GetCompanyAccountsRequest, GetCompanyAccountsResponse>(
	`/accounting/connection/${connectionId}/accounts`,
	'get',
	{ connectionId },
);

export const getAccountingSuppliers = (
	connectionId: number,
) => sendRequest<GetAccountingSuppliersRequest, GetAccountingSuppliersResponse>(
	`/accounting/connection/${connectionId}/suppliers`,
	'get',
	{ connectionId },
);

export const getCompanyAddresses = () => sendRequest<
	GetCompanyAddressesRequest, GetCompanyAddressesResponse>(
	'/invoices/company-address',
	'get',
	{},
);

export const createCompanyAddress = (
	attributes: CreateCompanyAddressAttributes,
) => sendRequest<CreateCompanyAddressRequest, CreateCompanyAddressResponse>(
	'/invoices/company-address', 'post', { attributes },
);

export const updateCompanyAddress = (id: number, attributes: UpdateCompanyAddressAttributes) => sendRequest<
	UpdateCompanyAddressRequest,
	UpdateCompanyAddressResponse
>(
	`/invoices/company-address/${id}`,
	'patch',
	{ id, attributes },
);

export const updateVoyagePayment = (
	id: number,
	voyageId: number,
	attributes: UpdateVoyagePaymentRequest['attributes'],
) => sendRequest<UpdateVoyagePaymentRequest, UpdateVoyagePaymentResponse>(
	`/voyages/${voyageId}/payments`,
	'patch',
	{ id, voyageId, attributes },
);

export const deleteVoyagePayment = (
	voyageId: number,
	id: number,
) => sendRequest<DeleteVoyagePaymentRequest, DeleteVoyagePaymentResponse>(
	`/voyages/${voyageId}/payments/${id}`,
	'delete',
	{ voyageId, id },
);

export const createUserCanalDefault = ({
	canalTransitName,
	canalTransitId,
	idleTime,
	cost,
	vesselType,
	vesselSize,
	applyToAllVessels,
}: {
	canalTransitName: string;
	canalTransitId: number;
	idleTime: number;
	cost: number;
	vesselType: string;
	vesselSize: string;
	applyToAllVessels: boolean;
}) => sendRequest<
	CreateOrgCanalDefaultsRequest,
	CreateOrgCanalDefaultsResponse
>(
	'/user/canal-default',
	'post',
	{ canalTransitName, canalTransitId, idleTime, cost, vesselType, vesselSize, applyToAllVessels },
);

export const getUserData = () => sendRequest<GetUserDataRequest, GetUserDataResponse>('/user');

export const deleteFixture = (id: number) => sendRequest<
	DeleteFixtureRequest,
	void
>(`/fixtures/${id}`, 'delete', { id });

export const updateFixture = (
	id: number,
	attributes: UpdateFixtureAttributes,
) => sendRequest<
	UpdateFixtureRequest,
	UpdateFixtureResponse
>(
	`/fixtures/${id}`,
	'patch',
	{ id, attributes },
);

export const updateTcTerms = (
	id: number,
	attributes: UpdateTcTermsRequest['attributes'],
) => sendRequest<
	UpdateTcTermsRequest,
	UpdateTcTermsResponse
>(
	`/fixtures/tc-terms/${id}`,
	'patch',
	{ id, attributes },
);

export const markFixtureAsUnfixed = (id: number) => sendRequest<
	MarkFixtureAsUnfixedRequest,
	MarkFixtureAsUnfixedResponse
>(
	`/fixtures/${id}/unfix`,
	'post',
	{ id },
);

export const renameIdentifier = (id: number, identifier?: string) => sendRequest<
	RenameIdentifierRequest,
	RenameIdentifierResponse
>(`/fixtures/${id}/regenerate-identifier`, 'post', { fixtureId: id, identifier });

export const createFixture = (
	vesselId: number,
	counterpartyId: number | undefined,
	identifier: string | undefined,
	fixtureType: Values<typeof FixtureTypes>,
	currency: Values<typeof Currencies>,
) => sendRequest<CreateFixtureRequest, CreateFixtureResponse>(
	'/fixtures',
	'post',
	{
		vesselId,
		counterpartyId,
		identifier,
		fixtureType,
		currency,
	},
);

export const createFixtureExpense = (
	tcTermsId: number,
	values: {
		account: Values<typeof AccountTypes>;
		name: string;
		amount: number;
	},
) => sendRequest<CreateFixtureExpenseRequest, CreateFixtureExpenseResponse>(
	`/fixtures/${tcTermsId}/expenses`,
	'post',
	{
		...values,
		tcTermsId,
	},
);

export const updateFixtureExpense = (
	tcTermsId: number,
	expenseId: number,
	attributes: UpdateFixtureExpenseRequest['attributes'],
) => sendRequest<UpdateFixtureExpenseRequest, UpdateFixtureExpenseResponse>(
	`/fixtures/${tcTermsId}/expenses/${expenseId}`,
	'patch',
	{ attributes, tcTermsId, expenseId },
);

export const getCounterparties = (type?: Values<typeof FixtureCounterpartyTypes>) => sendRequest<
	GetCounterpartiesRequest,
	GetCounterpartiesResponse
>(
	'/counterparties',
	'get',
	{ type },
);

export const getVesselsWithPositions = () => sendRequest<
	GetVesselsWithPositionsRequest,
	GetVesselsWithPositionsResponse
>(
	'/geo/vessels-with-positions',
	'get',
);

export const deleteFixtureExpense = (
	tcTermsId: number,
	expenseId: number,
) => sendRequest<DeleteFixtureExpenseRequest, DeleteFixtureExpenseResponse>(
	`/fixtures/${tcTermsId}/expenses/${expenseId}`,
	'delete',
	{
		tcTermsId,
		expenseId,
	},
);

export const deleteVesselOffHirePeriod = (
	vesselId: number,
	id: number,
) => sendRequest<DeleteVesselOffHirePeriodRequest, DeleteVesselOffHirePeriodResponse>(
	`/vessels/${vesselId}/off-hire/${id}`,
	'delete',
	{
		vesselId,
		id,
	},
);

export const getPorts = () => sendRequest<undefined, Port[]>('/geo/ports');

export const getPortsAndRanges = () => sendRequest<undefined, (Port | Range)[]>('/geo/ports-and-ranges');

export const getPortsInBounds = (
	bounds: GetPortsInBoundsRequest,
) => sendRequest<GetPortsInBoundsRequest, GetPortsInBoundsResponse>(
	'/geo/ports-in-bounds',
	'get',
	bounds,
);

export const getEstimateDetails = (
	id: number,
) => sendRequest<GetEstimateDetailsRequest, GetEstimateDetailsResponse>(
	`/estimates/${id}`,
	'get',
	{ id },
);

export const updateExpenseSubjectToHireDays = (
	tcTermsId: number,
	expenseId: number,
	attributes: {
		name: string;
		amount: number;
		interval: Values<typeof ExpensesSubjectToHireDaysIntervals>;
	},
) => sendRequest<UpdateExpenseSubjectToHireDaysRequest, UpdateExpenseSubjectToHireDaysResponse>(
	`/fixtures/${tcTermsId}/expenses-subject-to-hire-days/${expenseId}`,
	'patch',
	{ tcTermsId, expenseId, attributes },
);

export const getFuelEuCalculations = (
	estimateId: number,
) => sendRequest<GetFuelEuCalculationDetailsRequest, GetFuelEuDelatilsResponse>(
	`/estimates/fuel-eu-calculation/${estimateId}`,
	'get',
	{ estimateId },
);

export const addFuelEuPenaltyToEstimate = (
	estimateId: number,
	name: string,
) => sendRequest<CreateFuelEuRequest, GetFuelEuResponse>(
	`/estimates/${estimateId}/fuel-eu-penalty`, 'post', { estimateId, name },
);

export const updateFuelEuPenaltyToEstimate = (
	id: number,
	estimateId: number,
	attributes: UpdateFuelEuCalculationRequest['attributes'],
) => sendRequest<UpdateFuelEuCalculationRequest, void>(
	`/estimates/${estimateId}/fuel-eu-penalty/${id}`, 'patch', { id, estimateId, attributes },
);

export const deleteFuelEuCalculation = (
	estimateId: number,
	id: number,
) => sendRequest<DeleteFuelEuCalculationRequest, void>(
	`/estimates/${estimateId}/fuel-eu-penalty/${id}`,
	'delete',
	{ estimateId, id },
);

export const addEstimateCostToEstimate = (
	attributes: CreateEstimateCostAttributes,
) => sendRequest<CreateEstimateCostRequest, CreateEstimateCostResponse>(
	`/estimates/${attributes.estimateId}/estimate-cost`, 'post', { attributes },
);

export const updateEstimateCostToEstimate = (
	id: number, estimateId: number, attributes: UpdateEstimateCostAttributes,
) => sendRequest<
	UpdateEstimateCostRequest,
	UpdateEstimateCostResponse
>(
	`/estimates/${estimateId}/estimate-cost/${id}`, 'patch',
	{ id, estimateId, attributes },
);

export const getEstimateCost = (
	estimateId: number,
) => sendRequest<GetEstimateCostsRequest, GetEstimateCostsResponse>(
	`/estimates/estimate-cost/${estimateId}`,
	'get',
	{ estimateId },
);

export const deleteEstimateCost = (
	estimateId: number,
	id: number,
) => sendRequest<DeleteEstimateCostRequest, DeleteEstimateCostResponse>(
	`/estimates/${estimateId}/estimate-cost/${id}`,
	'delete',
	{ estimateId, id },
);

export const getEstimatedBrokerCommissions = (
	estimateId: number,
) => sendRequest<GetEstimatedBrokerCommissionsRequest, GetEstimatedBrokerCommissionsResponse>(
	`/estimates/${estimateId}/broker-commissions`,
	'get',
	{ estimateId },
);

export const getEstimatedDespatchAndDemurrage = (
	estimateId: number,
) => sendRequest<GetEstimatedDespatchAndDemurrageRequest, GetEstimatedDespatchAndDemurrageResponse>(
	`/estimates/${estimateId}/despatch-demurrage`,
	'get',
	{ estimateId },
);

export const addCargoToEstimate = (
	estimateId: number,
	cargoIds: number[],
) => sendRequest<AddCargoToEstimateRequest, AddCargoToEstimateResponse>(
	`/estimates/${estimateId}/cargo/add`, 'post', { cargoIds, estimateId },
);

export const calculateMultiRoute = (
	route: Array<{ name: string; latitude: number; longitude: number }>,
	settings: {},
) => sendRequest<CalculateMultiRouteRequest, CalculateMultiRouteResponse>(
	'/geo/calculate-multi-route',
	'post',
	{ route, settings },
);

export const getEstimates = () => sendRequest<GetEstimatesRequest, GetEstimatesResponse>(
	'/estimates',
	'get',
);

export const getCargos = (params?: GetCargosRequest) => sendRequest<GetCargosRequest, GetCargosResponse>(
	'/cargos',
	'get',
	// @ts-ignore
	params
);

export const createCargo = ({
	voyageId,
	estimateId,
	attributes,
}: CreateCargoRequest) => sendRequest<CreateCargoRequest, CreateCargoResponse>(
	'/cargos',
	'post',
	{ voyageId, estimateId, attributes, },
	{},
	{ savingIndicator: true },
);

export const createCargoRelet = ({
	estimateId,
}: CreateCargoReletRequest) => sendRequest<CreateCargoReletRequest, CreateCargoReletResponse>(
	'/cargos/relet',
	'post',
	{ estimateId },
	{},
	{ savingIndicator: true },
);

export const updateCargo = ({
	cargoId,
	attributes,
	mergeNewPorts,
}: {
	cargoId: number;
	attributes: UpdateCargoRequest['attributes'];
	mergeNewPorts?: boolean;
}, savingIndicator: boolean = true) => sendRequest<UpdateCargoRequest, UpdateCargoResponse>(
	'/cargos',
	'patch',
	{ cargoId, attributes, mergeNewPorts },
	{},
	{ savingIndicator },
);

export const updateCargoPort = ({
	cargoPortId,
	cargoId,
	attributes,
	mergePortInItinerary,
}: {
	cargoPortId: number;
	cargoId: number;
	attributes: UpdateCargoPortRequest['attributes'];
	mergePortInItinerary?: boolean;
}, savingIndicator: boolean = true) => sendRequest<UpdateCargoPortRequest, UpdateCargoPortResponse>(
	'/cargos/cargo-port',
	'patch',
	{ cargoPortId, cargoId, attributes, mergePortInItinerary },
	{},
	{ savingIndicator },
);

export const deleteCargo = (
	cargoId: number,
	estimateId?: number,
	savingIndicator: boolean = true,
) => sendRequest(
	'/cargos',
	'delete',
	{ cargoId, estimateId },
	{},
	{ savingIndicator },
);

export const deleteCargoRelet = (
	cargoId: number,
	estimateId?: number,
	savingIndicator: boolean = true,
) => sendRequest(
	'/cargos/relet',
	'delete',
	{ cargoId, estimateId },
	{},
	{ savingIndicator },
);

export const removeCargoFromContract = (
	cargoId: number
) => sendRequest<RemoveCargoFromContractRequest, RemoveCargoFromContractResponse>(
	'/cargos/remove-from-contract',
	'delete',
	{ cargoId },
	{},
	{ savingIndicator: true }
);

export const updateEstimate = (
	id: number,
	attributes: UpdateEstimateRequest['attributes'],
	updateWithoutAttributes?: boolean,
	savingIndicator: boolean = true,
) => sendRequest<UpdateEstimateRequest, UpdateEstimateResponse>(
	`/estimates/${id}`,
	'patch',
	{ id, attributes, updateWithoutAttributes },
	{},
	{ savingIndicator },
);

export const handleEstimateCommencementBunkers = (
	estimateId: number,
	bunkers: Array<SimpleBunker>
) => sendRequest<HandleEstimateCommencementBunkersRequest, HandleEstimateCommencementBunkersResponse>(
	`/estimates/${estimateId}/bunkers`,
	'patch',
	{ estimateId, bunkers },
	{},
	{ savingIndicator: true }
);

export const getVcPnl = (
	voyageId: number,
) => sendRequest<GetVcPnlRequest, GetVcPnlResponse>(
	`/voyages/${voyageId}/pnl`,
	'get',
	{ voyageId },
);

export const getTcPnl = (
	voyageId: number,
	inEstimate?: boolean,
) => sendRequest<GetTcPnlRequest, GetTcPnlResponse>(
	`/voyages/${voyageId}/tc/pnl`,
	'get',
	{ voyageId, inEstimate },
);

export const createEstimate = (
	attributes: CreateEstimateRequest['attributes'],
	estimateGroupId?: number,
	createEmptyCargo = true,
	existingCargoId?: number | null
) => sendRequest<CreateEstimateRequest, CreateEstimateResponse>(
	'/estimates/',
	'post',
	{ 
		attributes, 
		estimateGroupId, 
		createEmptyCargo, 
		existingCargoId 
	},
);

export const createContractFromEstimate = (
	id: number,
) => sendRequest<CreateContractFromEstimateRequest, CreateContractFromEstimateResponse>(
	`/estimates/${id}`,
	'post',
	{ id },
);

export const deleteEstimate = (
	id: number,
) => sendRequest<DeleteEstimateRequest, DeleteEstimateResponse>(
	`/estimates/${id}`,
	'delete',
	{ id },
);

export const deleteEstimateGroup = (
	id: number,
) => sendRequest<DeleteEstimateGroupRequest, DeleteEstimateGroupResponse>(
	`/estimates/group/${id}`,
	'delete',
	{ id },
);

export const createSubEstimate = (
	id: number,
	blank: boolean = false,
	cargoIds?: number[],
	estimateId?: number,
) => sendRequest<CreateSubEstimateRequest, CreateSubEstimateResponse>(
	`/estimates/copy/sub-estimate/${id}`, 'post', { id, blank, cargoIds, estimateId },
);

export const copyEstimate = (
	id: number,
) => sendRequest<CopyEstimateRequest, CopyEstimateResponse>(
	`/estimates/copy/estimate/${id}`, 'post', { id },
);

export const getFixtureDetails = <FixtureType extends TcFixtureProps | SpotFixtureProps | BbFixtureProps>(
	id: number,
) => sendRequest<GetFixtureDetailsRequest, GetFixtureDetailsResponse<FixtureType>>(
	`/fixtures/${id}`,
	'get',
	{ id },
);

export const getFixtures = (
	type: Values<typeof FixtureTypes> | undefined,
) => sendRequest<GetFixturesRequest, GetFixturesResponse>('/fixtures', 'get', { type });

export const getFixtureRecapPreview = (
	fixtureId: number,
	itemKeys: string[],
) => sendRequest<PreviewFixtureRecapRequest, PreviewFixtureRecapResponse>(
	`/fixtures/${fixtureId}/recap/preview`,
	'post',
	{ items: itemKeys, fixtureId },
);

export const getExport = (
	exportType: ExportTypes | null,
	attributes: InvoiceExportAttributes,
) => sendRequest<GetExportRequest, GetExportResponse>(
	'/misc/export',
	'post',
	{ exportType, attributes },
);

export const createFixtureRecap = (
	fixtureId: number,
	itemKeys: string[],
) => sendRequest<CreateFixtureRecapRequest, CreateFixtureRecapResponse>(
	`/fixtures/${fixtureId}/recap`,
	'post',
	{ items: itemKeys, fixtureId },
);

export const getBrokers = () => sendRequest<GetBrokersRequest, GetBrokersResponse>('/brokers');

export const getOrgBankAccounts = (
	orgOnly: boolean,
) => sendRequest<GetOrgBankAccountsRequest, GetOrgBankAccountsResponse>(
	'/organization/bank-accounts',
	'get',
	{ orgOnly },
);

export const getVesselBankAccounts = (
	vesselId: number,
	bankAccountType?: Values<typeof BankAccountTypes>,
) => sendRequest<GetVesselBankAccountsRequest, GetVesselBankAccountsResponse>(
	`/vessels/${vesselId}/bank-accounts`,
	'get',
	{
		vesselId,
		type: bankAccountType,
	},
);

export const createBankAccount = (
	type: Values<typeof BankAccountTypes>,
	vesselId?: number,
	fixingCompanyId?: number,
	savingIndicator: boolean = true,
) => sendRequest<CreateBankAccountRequest, CreateBankAccountResponse>(
	`/bank-accounts/${vesselId}`,
	'post',
	{ type, vesselId, fixingCompanyId },
	{},
	{ savingIndicator },
);

export const updateBankAccount = (
	attributes: UpdateBankAccountRequest['attributes'],
	accountId: number,
	vesselId?: number,
	savingIndicator: boolean = true,
) => sendRequest<UpdateBankAccountRequest, UpdateBankAccountResponse>(
	`/bank-accounts/${accountId}`,
	'patch',
	{ vesselId, accountId, attributes },
	{},
	{ savingIndicator },
);

export const deleteBankAccount = (
	accountId: number,
	vesselId?: number,
	savingIndicator: boolean = true,
) => sendRequest<DeleteBankAccountRequest, DeleteBankAccountResponse>(
	`/bank-accounts/${accountId}`,
	'delete',
	{ vesselId, accountId },
	{},
	{ savingIndicator },
);

export const refreshEuaPrice = (estimateId: number) => sendRequest<
	RefreshEuaPriceRequest,
	RefreshEuaPriceResponse
>(
	`/estimates/${estimateId}/refresh-eua`,
	'post',
	{ id: estimateId },
);

export const refreshVoyageEuaPrice = (id: number) => sendRequest<
	{ id: number },
	GetVoyageExpensesResponse['rows'][number]
>(
	`/voyages/${id}/refresh-eua`,
	'post',
	{ id },
);

export const createBroker = (
	name: string,
) => sendRequest<CreateBrokerRequest, CreateBrokerResponse>('/brokers', 'post', { name });

export const getVoyageCumulative = (
	voyageId: number,
) => sendRequest<GetVoyageCumulativeRequest, GetVoyageCumulativeResponse>(
	`/voyages/${voyageId}/cumulative`,
	'get',
	{ voyageId },
);

export const getVesselActivity = () => sendRequest<GetVesselActivityRequest, GetVesselActivityResponse>(
	'/vessels/activity',
);

export const reorderPortCall = ({
	vesselId,
	voyageId,
	draggedId,
	droppedOverId,
}: {
	voyageId: number;
	vesselId: number;
	draggedId: number;
	droppedOverId: number;
}) => sendRequest<ReorderPortCallRequest, ReorderPortCallResponse>(
	`/ops/itinerary/${vesselId}/reorder-port-call`,
	'post',
	{ draggedId, droppedOverId, vesselId, voyageId },
);

export const getVoyageExpenses = (params?: GetVoyageExpensesRequest) => sendRequest<
	GetVoyageExpensesRequest,
	GetVoyageExpensesResponse
>(
	'/invoices/voyage-expenses',
	'get',
	// @ts-ignore
	params,
);

export const getVoyageExpense = (id: number) => sendRequest<
	GetVoyageExpenseRequest,
	GetVoyageExpenseResponse
>(
	`/invoices/voyage-expenses/${id}`,
	'get',
	{ id },
);

export const getCargosAndLaytimes = ({
	voyageId,
	fixtureId
}: {
	voyageId: number;
	fixtureId?: number;
}) => sendRequest<GetCargosAndLaytimesRequest, GetCargosAndLaytimesResponse>(
	'/laytimes/',
	'get',
	{ fixtureId, voyageId },
);

export const createRobs = (props: {
	vesselId: number;
	voyageId: number;
	event: string;
	port: Port | null;
	date: Moment;
	currency: Values<typeof Currencies>;
	emptyEntry?: boolean;
	robs?: Array<{
		fuelGrade: Values<typeof FuelTypes>;
		quantity: number;
		pricePerTon?: number;
	}>;
	bunkersReceived?: Array<{
		fuelGrade: Values<typeof FuelTypes>;
		quantity: number;
		pricePerTon?: number;
	}>;
	portCallId?: number;
	cargoId?: number;
}) => sendRequest<CreateRobsRequest, CreateRobsResponse>(
	`/vessels/${props.vesselId}/bunker-robs`,
	'post',
	{ ...props },
);

export const getRobs = (
	vesselId: number,
	voyageId?: number,
) => sendRequest<GetRobsRequest, GetRobsResponse>(
	`/vessels/${vesselId}/bunker-robs`,
	'get',
	{ vesselId, voyageId },
);

export const getRobsFromPortCall = (
	vesselId: number,
	portCallId: number,
) => sendRequest<GetRobsFromPortCallRequest, GetRobsFromPortCallResponse>(
	`/vessels/${vesselId}/bunker-robs-from-portcall`,
	'get',
	{ vesselId, portCallId },
);

export const getTheShipSeaPassageDetails = (
	vesselId: number,
	seaPassageId: number,
) => sendRequest<GetTheShipSeaPassageDetailsRequest, GetTheShipSeaPassageDetailsResponse>(
	`/vessels/${vesselId}/the-ship/sea-passage/${seaPassageId}`,
	'get',
	{ vesselId, seaPassageId },
);
export const updateRob = ({
	voyageId,
	vesselId,
	portCallActionId,
	bunkerId,
	robBunkerId,
	currency,
	attributes,
	date,
	event,
	port,
	deleteMissingEntries,
	resetQueue,
	showError,
}: {
	vesselId: number;
	portCallActionId: number;
	attributes: {
		bunkersReceived?: Array<NullableBunkerRecord>;
		remainingOnBoard?: Array<Partial<BunkerRecord>> | Partial<BunkerRecord> | Array<CommencementRobBunker>;
	};
	voyageId: number;
	event?: string | null;
	port?: Port;
	date?: Moment;
	robBunkerId?: number;
	currency?: Values<typeof Currencies>;
	bunkerId?: number;
	deleteMissingEntries?: boolean;
	resetQueue?: boolean;
	showError?: boolean;
}, savingIndicator: boolean = true) => sendRequest<UpdateRobRequest, UpdateRobResponse>(
	`/vessels/${vesselId}/bunker-robs`,
	'patch',
	{
		portCallActionId,
		vesselId,
		bunkerId,
		robBunkerId,
		currency,
		attributes,
		date,
		event,
		port,
		voyageId,
		deleteMissingEntries,
		resetQueue,
		showError,
	},
	{},
	{ savingIndicator },
);

export const updateRobBatch = (
	vesselId: number,
	voyageId: number,
	updates: UpdateRobBatchRequest['updates'],
	savingIndicator: boolean = true,
) => sendRequest<UpdateRobBatchRequest, UpdateRobBatchResponse>(
	`/vessels/${vesselId}/bunker-robs/batch`,
	'patch',
	{
		vesselId,
		voyageId,
		updates,
	},
	{},
	{ savingIndicator },
);

export const deleteRobBunker = (
	robBunkerId: number,
	vesselId: number,
) => sendRequest<DeleteRobBunkerRequest, DeleteRobBunkerResponse>(
	`/vessels/rob-bunker/${vesselId}`,
	'delete',
	{ robBunkerId, vesselId },
);

export const createHireInvoice = (
	voyageId: number,
	items: HireInvoiceItem[],
	extra: {
		cumulative: boolean;
		invoiceTitle: string;
		invoiceDate: Moment;
		dueDateOverride?: Moment;
		chartererId?: number;
		showPreviousItems?: boolean;
		showPaymentTerms?: boolean;
		showOnBehalfOf?: boolean;
		groupHirePeriods?: boolean;
		cargoId?: number;
		fromBankAccountId?: number;
		isCreditNote?: boolean;
		isEua?: boolean;
		isCargoRelet: boolean;
	},
) => sendRequest<CreateHireInvoiceRequest, CreateHireInvoiceResponse>(
	'/invoices/hire-invoices',
	'post',
	{
		voyageId,
		items,
		...extra,
	},
);

export const getRequiredApprovals = () => sendRequest<
	GetRequiredApprovalsRequest,
	GetRequiredApprovalsResponse
>('/organization/approvals', 'get', {});

export const updateOrganizationDetails = (
	attributes: {
		approvalsList?: Array<ApprovalItem>;
		defaultSeaMargin?: number;
		capexOpexInput?: CapexOpexInput;
		portPurposeList?: Array<PortPurposeItem>
		defaultEstimatorLinerCostUnit?: LinerCostUnitDefaults;
	},
) => sendRequest<UpdateOrganizationDetailsRequest, UpdateOrganizationDetailsResponse>(
	'/organization',
	'patch',
	{ attributes },
	{},
	{ savingIndicator: true },
);

export const getAccountingItemsForApproval = () => sendRequest<
	GetAccountingItemsForApprovalRequest,
	GetAccountingItemsForApprovalResponse
>(
	'/invoices/accounting-items',
	'get',
	{},
);

export const getPortPurposes = () => sendRequest<
	GetPortPurposesRequest,
	GetPortPurposesResponse
>('/organization/port-purposes', 'get', {});

export const submitHireInvoice = (invoiceId: number) => sendRequest<
	SubmitHireInvoiceRequest,
	SubmitHireInvoiceResponse
>(
	`/invoices/hire-invoices/${invoiceId}/submit`,
	'post',
	{ invoiceId },
);

export const approveHireInvoice = (invoiceId: number, note: string) => sendRequest<
	ApproveHireInvoiceRequest,
	ApproveHireInvoiceResponse
>(
	`/invoices/hire-invoices/${invoiceId}/approve`,
	'post',
	{ invoiceId, note },
);

export const rejectHireInvoice = (invoiceId: number, reason: string) => sendRequest<
	RejectHireInvoiceRequest,
	RejectHireInvoiceResponse
>(
	`/invoices/hire-invoices/${invoiceId}/reject`,
	'post',
	{ invoiceId, reason },
);

export const unsubmitHireInvoice = (invoiceId: number) => sendRequest<
	UnsubmitHireInvoiceRequest,
	UnsubmitHireInvoiceResponse
>(
	`/invoices/hire-invoices/${invoiceId}/unsubmit`,
	'post',
	{ invoiceId },
);

export const toggleUserDisabled = (orgId: number, userId: number) => sendRequest<
	ToggleUserDisabledRequest,
	ToggleUserDisabledResponse
>(
	`/admin/organizations/${orgId}/users/${userId}/toggle-disabled`,
	'patch',
	{ userId },
);

export const sendUserInvitation = (orgId: number, userId: number) => sendRequest<
	SendUserInvitationRequest,
	SendUserInvitationResponse
>(
	`/admin/organizations/${orgId}/users/${userId}/invite`,
	'patch',
	{ userId },
);

export const getUserInvitationLink = (orgId: number, userId: number) => sendRequest<
	GetUserInvitationLinkRequest,
	GetUserInvitationLinkResponse
>(
	`/admin/organizations/${orgId}/users/${userId}/invitation-link`,
	'post',
	{ id: orgId, userId },
);

export const setupUserPasswordLogin = (orgId: number, userId: number) => sendRequest<
	SetupUserPasswordLoginRequest,
	SetupUserPasswordLoginResponse
>(
	`/admin/organizations/${orgId}/users/${userId}/password-login`,
	'post',
	{ id: orgId, userId },
);

export const getUsers = () => sendRequest<GetUserDataRequest, GetUsersResponse>('/organization/users');

export const updateUser = ({
	userId,
	attributes,
}: {
	userId?: number;
	attributes: UserAttributes;
}) => sendRequest<
	UpdateUserRequest,
	UpdateUserResponse
>(
	'/user',
	'patch',
	{ userId, attributes },
);

export const updateUserDepartments = ({
	userId,
	departments,
}: { userId: number; departments: DepartmentKey[] }) => sendRequest<
	UpdateUserDepartmentsRequest,
	UpdateUserDepartmentsResponse
>(
	'/organization/departments',
	'patch',
	{ userId, departments },
	{},
	{ savingIndicator: true },
);

export const inviteTeamMember = (data: {
	name: string;
	email: string;
	external: boolean;
	departments: DepartmentKey[];
}) => sendRequest<
	InviteTeamMemberRequest,
	InviteTeamMemberResponse
>(
	'/organization/invite-member',
	'post',
	data,
);

export const disableTeamMember = (userId: number) => sendRequest<
	RemoveTeamMemberRequest,
	RemoveTeamMemberResponse
>(
	`/organization/users/${userId}/disable`,
	'patch',
	{ id: userId },
);

export const createCargoType = ({
	name,
	group,
	stowageFactorCBM,
	stowageFactorCBFT,
	active,
	vesselTypes,
}: {
	name: string;
	group?: string | null;
	stowageFactorCBM?: number | null;
	stowageFactorCBFT?: number | null;	
	active: boolean;
	vesselTypes?: Values<typeof VesselTypes>[];
}) => sendRequest<
	CreateCargoTypeRequest,
	void
>(
	'/user/cargo-type',
	'post',
	{ name, group, stowageFactorCBM, stowageFactorCBFT, active, vesselTypes },
);

export const updateCargoType = ({
	id,
	name,
	group,
	stowageFactorCBM,
	stowageFactorCBFT,
	active,
	vesselTypes,
}: {
	id: number;
	name: string;
	group?: string | null;
	stowageFactorCBM?: number | null;
	stowageFactorCBFT?: number | null;
	active: boolean;
	vesselTypes?: Values<typeof VesselTypes>[];
}) => sendRequest<
	UpdateCargoTypeRequest,	
	void
>(
	'/user/cargo-type',
	'patch',
	{ id, name, group, stowageFactorCBM, stowageFactorCBFT, active, vesselTypes },
);

export const deleteCargoType = ({
	id,
}: {
	id: number;
}) => sendRequest<
	{ id: number },
	void
>(
	'/user/cargo-type',
	'delete',
	{ id },
);

export const updateUserCanalDefault = ({
	id,
	canalTransitName,
	canalTransitId,
	idleTime,
	cost,
	vesselType,
	vesselSize,
	applyToAllVessels,
}: {
	id: number;
	canalTransitName: string;
	canalTransitId: number;
	idleTime: number;
	cost: number;
	vesselType: string;
	vesselSize: string;
	applyToAllVessels: boolean;
}) => sendRequest<
	UpdateOrgCanalTransitDefaultsRequest,
	void
>(
	'/user/canal-default',
	'patch',
	{
		id,
		canalTransitName,
		canalTransitId,
		idleTime,
		cost,
		vesselType,
		vesselSize,
		applyToAllVessels
	},
);

export const deleteUserCanalDefault = ({
	id,
}: {
	id: number;
}) => sendRequest<
	{ id: number },
	void
>(
	'/user/canal-default',
	'delete',
	{ id },
);

export const createBrokerInvoice = ({
	values,
	attachments,
}: {
	values: Omit<CreateBrokerInvoiceRequest, 'attachments'>;
	attachments: File[];
}) => sendRequestWithFiles<
	CreateBrokerInvoiceRequest & { attachments?: File[] },
	CreateBrokerInvoiceResponse
>(
	`/brokers/invoices/${values.brokerId}`,
	'post',
	values,
	attachments,
);

export const deleteBrokerInvoice = (brokerInvoiceId: number) => sendRequest<
	DeleteBrokerInvoiceRequest,
	DeleteBrokerInvoiceResponse
>(
	`/brokers/invoices/${brokerInvoiceId}`,
	'delete',
	{ brokerInvoiceId },
);

export const updateBrokerInvoice = (params: {
	brokerInvoiceId: number;
	itemDescription: string;
	dateReceived: Moment;
	dueDate: Moment;
	amount: number;
	currency: Values<typeof Currencies>;
	exchangeRate: number;
	attachments: any;
	invoiceId: string;
}) => {
	const data = new FormData();

	if (params.attachments != null) {
		params.attachments.forEach((a: any) => data.append('attachments', a));
	}

	Object.entries(params).forEach(([key, value]) => {
		if (value != null) {
			data.append(key, value);
		}
	});

	return sendRequest<
		UpdateBrokerInvoiceRequest,
		UpdateBrokerInvoiceResponse
	>(
		`/brokers/invoices/${params.brokerInvoiceId}`,
		'patch',
		// @ts-ignore requires typing of multi-part-file-upload (i think?)
		data,
	);
};

export const submitBrokerInvoice = (brokerInvoiceId: number) => sendRequest<
	SubmitBrokerInvoiceRequest,
	SubmitBrokerInvoiceResponse
>(
	`/brokers/invoices/${brokerInvoiceId}/submit`,
	'post',
	{ brokerInvoiceId },
);

export const unsubmitBrokerInvoice = (brokerInvoiceId: number) => sendRequest<
	UnsubmitBrokerInvoiceRequest,
	UnsubmitBrokerInvoiceResponse
>(
	`/brokers/invoices/${brokerInvoiceId}/unsubmit`,
	'post',
	{ brokerInvoiceId },
);

export const approveBrokerInvoice = (brokerInvoiceId: number, note?: string) => sendRequest<
	ApproveBrokerInvoiceRequest,
	ApproveBrokerInvoiceResponse
>(
	`/brokers/invoices/${brokerInvoiceId}/approve`,
	'post',
	{ brokerInvoiceId, note },
);

export const rejectBrokerInvoice = (brokerInvoiceId: number, reason?: string) => sendRequest<
	RejectBrokerInvoiceRequest,
	RejectBrokerInvoiceResponse
>(
	`/brokers/invoices/${brokerInvoiceId}/reject`,
	'post',
	{ brokerInvoiceId, reason },
);

export const updateVoyageExpense = (invoiceId: number, attributes: Partial<{
	itemDescription: string;
	amount: number;
	supplierId: number;
	invoiceDate: Date;
	customInvoiceId: string;
	dueDate: Date;
	note: string;
	currency: Values<typeof Currencies>;
	exchangeRate: number;
	isPortDA: boolean;
	attachments: any;
	estimated: boolean;
	port: Port;
	isFuelEuPenalty: boolean;
	isEuEts: boolean;
	allocatedToId?: number | null;
	euCalculationRows: object[];
	ukCalculationRows: object[];
	fuelEuCalculationRows: object[];
	euaActualPrice: number | null;
	ukaActualPrice: number | null;
	euaExchangeRate: number | null;
	fuelEuExchangeRate: number | null;
	overrideAmount: number | null;
	isIceClass: boolean;
}>) => {
	const data = new FormData();

	if (attributes.attachments != null) {
		attributes.attachments.forEach((a: any) => data.append('attachments', a));
	}

	const parameters = {
		...attributes,
		invoiceId,
		invoiceDate: attributes.invoiceDate && formatDate(attributes.invoiceDate),
		dueDate: attributes.dueDate && formatDate(attributes.dueDate),
	};

	// Add all not-null values to form data

	Object.entries(parameters).forEach(([key, value]) => {
		if (key === 'allocatedToId' && value === null) {
			data.append(key, '');

			return;
		}

		if (key === 'attachments') {
			return;
		}

		// Unless if it's overrideAmount, which can be null and 
		// should be sent as a string 'null' if it is null
		if (key === 'overrideAmount') {
			data.append(
				key,
				value == null ?
					'null' :
					String(value),
			);

			return;
		}

		if (value == null) {
			return;
		}

		// Calculation rows are arrays and must be serialized before adding to FormData
		if (
			key === 'euCalculationRows' ||
			key === 'ukCalculationRows' ||
			key === 'fuelEuCalculationRows'
		) {
			data.append(key, JSON.stringify(value));

			return;
		}

		data.append(key, value as any);
	});

	return sendRequest<UpdateVoyageExpenseRequest, UpdateVoyageExpenseResponse>(
		`/invoices/voyage-expenses/${invoiceId}`,
		'patch',
		// @ts-ignore requires typing of multi-part-file-upload (i think?)
		data,
	);
};

export const sendAdminInvitation = (
	id: number,
	name: string,
	email: string,
	external: boolean,
	isManagement: boolean,
) => sendRequest<
	SendAdminInvitationRequest,
	SendAdminInvitationsResponse
>(
	`/admin/organizations/${id}/invite/admin`,
	'post',
	{ id, name, email, external, isManagement },
);

export const sendUserInvitations = (
	id: number,
	users: Array<UserInvite>,
) => sendRequest<
	SendUserInvitationsRequest,
	SendUserInvitationsResponse
>(
	`/admin/organizations/${id}/invite/users`,
	'post',
	{ id, users },
);

export const sendWelcomeEmail = (id: number, userId: number) => sendRequest<
	SendWelcomeEmailRequest,
	SendWelcomeEmailResponse
>(
	`/admin/organizations/${id}/welcome/${userId}`,
	'post',
	{ id, userId },
);

export const updateOrganizationUser = (
	userId: number,
	orgId: number,
	attributes: Partial<UpdateOrganizationUserRequest['attributes']>,
) => sendRequest<
	UpdateOrganizationUserRequest,
	UpdateOrganizationUserResponse
>(
	`/admin/organizations/${orgId}/users/${userId}`,
	'patch',
	{ attributes, userId },
);

export const updateAttachment = (attachmentId: number, attributes: Attributes) => sendRequest<
	UpdateAttachmentRequest,
	UpdateAttachmentResponse
>(
	`/attachments/${attachmentId}`,
	'patch',
	{ attributes, id: attachmentId },
);

export const addAttachment = (belongsToType: Values<typeof AttachmentTypes>, belongsToId: number, file: File) => {
	const data = new FormData();

	data.append('attachment', file);
	data.append('attachedToType', belongsToType);
	data.append('attachedToId', belongsToId.toString());

	return sendRequest<AddAttachmentRequest, AddAttachmentResponse>(
		'/attachments',
		'post',
		// @ts-ignore FormData can't be typed?
		data,
	);
};

export const activateAccount = (token: string) => sendRequest<
	ActivateOrganizationRequest,
	ApproveBrokerInvoiceResponse
>(
	`/auth/activation/${token}`,
	'patch',
	{ token },
);

export const getActivationDetails = (token: string) => sendRequest<
	GetActivationDetailsRequest,
	GetActivationDetailsResponse
>(
	`/auth/activation/${token}`,
	'get',
	{ token },
);

export const updateBroker = (id: number, attributes: UpdateBrokerAttributes) => sendRequest<
	UpdateBrokerRequest,
	UpdateBrokerResponse
>(
	`/brokers/${id}`,
	'patch',
	{ id, attributes },
);

export const getBrokerDetails = (brokerId: number) => sendRequest<
	GetBrokerDetailsRequest,
	GetBrokerDetailsResponse
>(
	`/brokers/${brokerId}`,
	'get',
	{ id: brokerId },
);

export const deleteBroker = (id: number) => sendRequest<
	DeleteBrokerRequest,
	DeleteBrokerResponse
>(
	`/brokers/${id}`,
	'delete',
	{ id },
);

export const createCounterparty = (type: Values<typeof FixtureCounterpartyTypes>, name: string) => sendRequest<
	CreateCounterpartyRequest,
	CreateCounterpartyResponse
>(
	'/counterparties',
	'post',
	{ type, name },
);

export const getCounterpartyDetails = (id: number) => sendRequest<
	GetCounterpartyDetailsRequest,
	GetCounterpartyDetailsResponse
>(
	`/counterparties/${id}`,
	'get',
	{ id },
);

export const deleteCounterparty = (id: number) => sendRequest<
	DeleteCounterpartyRequest,
	DeleteCounterpartyResponse
>(
	`/counterparties/${id}`,
	'delete',
	{ id },
);

export const getAccrualItems = () => sendRequest<GetAccrualItemsRequest, GetAccrualItemsResponse>(
	'/financials/accrual-items',
	'get',
	{},
);

export const getPreviousCloses = () => sendRequest<GetClosedPeriodsRequest, GetClosedPeriodsResponse>(
	'/financials/previous-closes',
	'get',
	{},
);

export const updateAccrualItem = (id: number, attributes: { shouldProrate?: boolean }) => sendRequest<
	UpdateAccrualItemRequest,
	UpdateAccrualItemResponse
>(
	'/financials/accrual-items',
	'patch',
	{ id, attributes },
	{},
	{ savingIndicator: true },
);

export const getVideoGuides = (pageTitle?: typeof RootPages[number]) => sendRequest<
	GetVideoGuidesRequest,
	GetVideoGuidesResponse
>(
	'/video-guides',
	'get',
	{ pageTitle },
);

export const createVideoGuide = ({
	title,
	description,
	url,
	page,
}: CreateVideoGuideRequest) => sendRequest<CreateVideoGuideRequest, CreateVideoGuideResponse>(
	'/admin/video-guides',
	'post',
	{ title, description, url, page },
	{},
	{ savingIndicator: true },
);

export const updateVideoGuide = ({
	id,
	attributes,
}: {
	id: number;
	attributes: UpdateVideoGuideRequest['attributes'];
}) => sendRequest<UpdateVideoGuideRequest, UpdateVideoGuideResponse>(
	'/admin/video-guides',
	'patch',
	{ id, attributes },
	{},
	{ savingIndicator: true },
);

export const updateVideoGuideOrder = (
	reorderedEntries: Array<VideoGuideProps>,
) => sendRequest<UpdateVideoGuideOrderRequest, UpdateVideoGuideOrderRequest>(
	'/admin/video-guides/reorder',
	'patch',
	{ reorderedEntries },
);

export const deleteVideoGuide = (id: number) => sendRequest<DeleteVideoGuideRequest, DeleteVideoGuideResponse>(
	'/admin/video-guides',
	'delete',
	{ id },
	{},
	{ savingIndicator: true },
);

export const getVoyageDurations = (
	voyageIds: number[],
) => sendRequest<GetVoyageDurationRequest, GetVoyageDurationResponse>(
	`/voyages/duration/${voyageIds.join(',')}`,
	'get',
	{ voyageIds },
);

export const createRevenueSubjectToDays = (
	fixtureId: number,
	{ name, amount, accountCodeOverride }: { name: string; amount: number; accountCodeOverride?: string | null },
) => sendRequest(
	`/fixtures/${fixtureId}/revenue-subject-to-days`,
	'post',
	{ name, amount, accountCodeOverride },
);

export const syncPayments = (
	voyageId: number,
) => sendRequest(
	`/accounting/sync-payments/${voyageId}`,
	'post',
	{ voyageId },
);

export const syncExpensePayments = (
	expenseId: number,
) => sendRequest(
	`/accounting/sync-expense-payments/${expenseId}`,
	'post',
	{ expenseId },
);

export const updateRevenueSubjectToDays = (
	fixtureId: number,
	revenueId: number,
	attributes: { name: string; amount: number; accountCodeOverride?: string | null },
) => sendRequest(
	`/fixtures/${fixtureId}/revenue-subject-to-days/${revenueId}`,
	'patch',
	{ attributes },
);

export const deleteRevenueSubjectToDays = (
	fixtureId: number,
	revenueId: number,
) => sendRequest(
	`/fixtures/${fixtureId}/revenue-subject-to-days/${revenueId}`,
	'delete',
);

export const createExpenseSubjectToHireDays = (
	fixtureId: number,
	{
		name,
		amount,
		interval,
		accountCodeOverride,
	}: {
		name: string;
		amount: number;
		interval: string;
		accountCodeOverride?: string | null;
	},
) => sendRequest(
	`/fixtures/${fixtureId}/expenses-subject-to-hire-days`,
	'post',
	{
		name,
		amount,
		interval,
		accountCodeOverride,
	},
);

export const deleteExpenseSubjectToHireDays = (
	fixtureId: number,
	expenseId: number,
) => sendRequest(
	`/fixtures/${fixtureId}/expenses-subject-to-hire-days/${expenseId}`,
	'delete',
);

export const getReportGroups = () => sendRequest<GetReportGroupsRequest, GetReportGroupsResponse>(
	'/organization/report-groups',
	'get',
);

export const getReceivablesAgingData = (period: DaysDashboardPeriods) => sendRequest<
	GetReceivablesAgingDataRequest,
	GetReceivablesAgingDataResponse
>(
	'/statistics/receivables-aging',
	'get',
	{ period },
);

export const getPayablesAgingData = (period: DaysDashboardPeriods) => sendRequest<
	GetPayablesAgingDataRequest,
	GetPayablesAgingDataResponse
>(
	'/statistics/payables-aging',
	'get',
	{ period },
);

export const updateReportGroups = (
	attributes: ReportGroupAttributes,
) => sendRequest<UpdateReportGroupsRequest, UpdateReportGroupsResponse>(
	'/organization/report-groups',
	'post',
	{ attributes },
);

export const updateOffHireTypes = (
	attributes: UpdateOffHireTypesAttributes,
) => sendRequest<UpdateOffHireTypesRequest, UpdateOffHireTypesResponse>(
	'/organization/off-hire-types',
	'post',
	{ attributes },
);

export const updatePortCostCategories = (
	portCostCategories: string[],
) => sendRequest<UpdatePortCostCategoriesRequest, UpdatePortCostCategoriesResponse>(
	'/organization/port-cost-categories',
	'post',
	{ portCostCategories },
);

export const createDisbursementAccount = ({
	values,
	attachments,
}: {
	values: Omit<CreateDisbursementAccountRequest, 'attachments'>;
	attachments: File[];
}) => sendRequestWithFiles<
	CreateDisbursementAccountRequest & { attachments?: File[] },
	CreateDisbursementAccountResponse
>(
	'/invoices/disbursement-accounts',
	'post',
	values,
	attachments,
);

export const updateDisbursementAccount = ({
	values,
	attachments,
}: {
	values: UpdateDisbursementAccountRequest;
	attachments: File[];
}) => sendRequestWithFiles<
	UpdateDisbursementAccountRequest & { attachments?: File[] },
	UpdateDisbursementAccountResponse
>(
	'/invoices/disbursement-accounts',
	'patch',
	values,
	attachments,
);

export const deleteDisbursementAccount = (
	daId: number,
) => sendRequest<DeleteDisbursementAccountRequest, DeleteDisbursementAccountResponse>(
	'/invoices/disbursement-accounts',
	'delete',
	{ daId },
);

export const getDisbursementAccounts = (
	voyageId?: number,
) => sendRequest<GetDisbursementAccountsRequest, GetDisbursementAccountsResponse>(
	'/invoices/disbursement-accounts',
	'get',
	{ voyageId },
);

export const getDisbursementAccount = (
	id: number,
) => sendRequest<GetDisbursementAccountRequest, GetDisbursementAccountResponse>(
	`/invoices/disbursement-accounts/${id}`,
	'get',
	{ id },
);

export const getHistoricalDisbursementAccounts = (
	portId: number,
	page?: number,
) => sendRequest<GetHistoricalDisbursementAccountsRequest, GetHistoricalDisbursementAccountsResponse>(
	'/invoices/disbursement-accounts/historical',
	'get',
	{ portId, page },
);

export const getVoyageItinerary = (
	voyageId: number,
) => sendRequest<GetVoyageItineraryRequest, GetVoyageItineraryResponse>(
	`/voyages/${voyageId}/itinerary`,
	'get',
	{ voyageId },
);

export const getPreviousVoyageBunkers = (
	voyageId: number,
) => sendRequest<GetPreviousVoyageBunkersRequest, GetPreviousVoyageBunkersResponse>(
	`/voyages/${voyageId}/previous-bunkers`,
	'get',
	{ voyageId },
);

export const getVoyageInvoices = (voyageId: number, options?: { includeDeleted?: boolean }) => sendRequest<
	GetVoyageInvoicesRequest,
	GetVoyageInvoicesResponse
>(
	'/invoices/voyage-invoices',
	'get',
	{ voyageId, includeDeleted: options?.includeDeleted },
);

export const updateVesselConsumptionEntries = ({
	vesselId,
	attributes,
}: {
	vesselId: number;
	attributes: Array<{
		id?: number;
		consumption?: number;
		fuelType?: Values<typeof FuelTypes>;
	}>;
}) => sendRequest<UpdateVesselConsumptionEntryRequest, UpdateVesselConsumptionEntryResponse>(
	`/vessels/${vesselId}/consumption-entries`,
	'patch',
	{ attributes, vesselId },
	{},
	{ savingIndicator: true },
);

export const createVesselOtherConsumptionSet = (
	vesselId: number,
	condition?: string,
	consumption?: number,
	fuelType?: Values<typeof FuelTypes>,
) => sendRequest<
	CreateVesselOtherConsumptionSetRequest,
	CreateVesselOtherConsumptionSetResponse
>(
	`/vessels/${vesselId}/other-consumption`,
	'post',
	{ vesselId, condition, consumption, fuelType },
);

export const updateVesselOtherConsumptionEntries = ({
	vesselId,
	attributes,
}: {
	vesselId: number;
	attributes: Array<{
		id?: number;
		consumption?: number;
		condition?: string;
		fuelType?: Values<typeof FuelTypes>;
	}>;
}) => sendRequest<UpdateVesselOtherConsumptionEntryRequest, UpdateVesselOtherConsumptionEntryResponse>(
	`/vessels/${vesselId}/other-consumption`,
	'patch',
	{ attributes, vesselId },
	{},
	{ savingIndicator: true },
);

export const createVesselConsumptionSet = (vesselId: number, estimateId?: number | null) => sendRequest<
	CreateVesselConsumptionSetRequest,
	CreateVesselConsumptionSetResponse
>(
	`/vessels/${vesselId}/consumption-sets`,
	'post',
	{ vesselId, estimateId },
);

export const updateVesselConsumptionSet = ({
	vesselId,
	consumptionSetId,
	attributes,
}: {
	vesselId: number;
	consumptionSetId: number;
	attributes: {
		enableSecondary?: boolean;
		name?: string;
		ballastSpeed?: number;
		ladenSpeed?: number;
		estimateId?: number | null;
	};
}) => sendRequest<
	UpdateVesselConsumptionSetRequest,
	UpdateVesselConsumptionSetResponse
>(
	`/vessels/${vesselId}/consumption-sets`,
	'patch',
	{ vesselId, consumptionSetId, attributes },
);

export const deleteVesselOtherPerformanceEntry = (vesselId: number, otherPerformanceEntryId: number) => sendRequest<
	DeleteVesselOtherPerformanceEntryRequest,
	DeleteVesselOtherPerformanceEntryResponse
>(
	`/vessels/${vesselId}/other-consumption/${otherPerformanceEntryId}`,
	'delete',
	{ vesselId, otherPerformanceEntryId },
);

export const deleteVesselConsumptionSet = (consumptionSetId: number, vesselId: number) => sendRequest<
	DeleteVesselConsumptionSetRequest,
	DeleteVesselConsumptionSetResponse
>(
	`/vessels/${vesselId}/consumption-sets`,
	'delete',
	{ vesselId, consumptionSetId },
);

export const getOutstandingsData = (
	eua?: boolean,
) => sendRequest<GetOutstandingsDataRequest, GetOutstandingsDataResponse>(
	'/statistics/outstandings',
	'get',
	{ eua },
);

export const getFullFleetTimelineData = () => sendRequest<
	GetFullFleetTimelineDataRequest,
	GetFullFleetTimelineDataResponse
>(
	'/statistics/full-fleet-timeline-data',
	'get',
);

export const createCustomField = ({ type, value, label, parentType, parentId, voyageId, customFieldId }
: {
	type: CustomFieldType; value: CustomFieldValueTypes[CustomFieldType]; label: string; parentType:
	CustomFieldParentTypes; parentId: number; voyageId: number | null; customFieldId: number | null;
}) => sendRequest<
	CreateCustomFieldRequest, CreateCustomFieldResponse>(
	'/misc/custom-field',
	'post',
	{ type, value, label, parentType, parentId, voyageId, customFieldId },
);

export const getCustomFields = ({ parentType, parentId, voyageId }
: { parentType: CustomFieldParentTypes; parentId: number; voyageId?: number }) => sendRequest<
	GetCustomFieldsRequest, GetCustomFieldsResponse>(
	'/misc/custom-field',
	'get',
	{ parentType, parentId, voyageId },
);

export const deleteCustomField = (id: number) => sendRequest<DeleteCustomFieldRequest, DeleteCustomFieldResponse>(
	`/misc/custom-field/${id}`,
	'delete',
	{ id },
);

export const updateCustomField = ({ id, type, label, value, voyageId }
: {
	id: number;
	type: CustomFieldType;
	label: string; value: string | number | Date;
	voyageId?: number;
}) => sendRequest<
	UpdateCustomFieldRequest, UpdateCustomFieldResponse>(
	`/misc/custom-field/${id}`,
	'patch',
	{ id, type, label, value, voyageId },
);

export const updatePortRotation = (
	updatedPortRotation: PartialPortRotationEntry[],
	estimateId: number,
) => sendRequest<UpdatePortRotationRequest, UpdatePortCallResponse>(
	'/estimates/reorder',
	'post',
	{ estimateId, updatedPortRotation },
);

export const getEstimateGroup = (
	estimateGroupId: number,
) => sendRequest<GetEstimateGroupRequest, GetEstimateGroupResponse>(
	`/estimates/group/${estimateGroupId}`,
	'get',
	{ estimateGroupId },
);

export const getEstimateGroupVessels = (
	estimateGroupId: number,
) => sendRequest<GetEstimateGroupVesselsRequest, GetEstimateGroupVesselsResponse>(
	`/estimates/group/${estimateGroupId}/vessels`,
	'get',
	{ estimateGroupId },
);

export const getContractLinkMissingData = (
	estimateId: number,
	previousVoyageId: number,
) => sendRequest<GetContractLinkMissingDataRequest, GetContractLinkMissingDataResponse>(
	`/estimates/${estimateId}/contract-link/${previousVoyageId}/missing-data`,
	'get',
	{ estimateId, previousVoyageId },
);

export const getEstimateGroupCargos = (
	estimateGroupId: number,
) => sendRequest<GetEstimateGroupCargosRequest, GetEstimateGroupCargosResponse>(
	`/estimates/group/${estimateGroupId}/cargos`,
	'get',
	{ estimateGroupId },
);

export const getEstimateGroups = (
	params?: GetEstimateGroupsRequest,
) => sendRequest<GetEstimateGroupsRequest, GetEstimateGroupsResponse>(
	'/estimates/groups',
	'get',
	// @ts-ignore
	params,
);

export const createBbItem = ({ fixtureId, attributes }:
{
	fixtureId: number;
	attributes: {
		type: Values<typeof BareboatItemType>;
		amount: number;
		pcm?: boolean;
		addComm?: number | null;
		brokerPaidByOwnerIds?: Array<number | null>;
		brokerPaidByOwnerPercentages?: Array<number | null>;
		brokerPaidByChartererIds?: Array<number | null>;
		brokerPaidByChartererPercentages?: Array<number | null>;
	};
}) => sendRequest<CreateBbItemRequest, CreateBbItemResponse>(
	`/fixtures/${fixtureId}/bb-items`,
	'post',
	{ fixtureId, attributes },
);

export const getPnlOverride = (voyageId: number) => sendRequest<GetPnlOverrideRequest, GetPnlOverrideResponse>(
	`/voyages/${voyageId}/pnl-override`,
	'get',
	{ voyageId },
);

export const updatePnlOverride = ({
	voyageId,
	overrideData,
}: {
	voyageId: number;
	overrideData: UpdatePnlOverrideRequest['overrideData']
}) => sendRequest<
	UpdatePnlOverrideRequest,
	UpdatePnlOverrideResponse
>(
	`/voyages/${voyageId}/pnl-override`,
	'patch',
	{ voyageId, overrideData },
);

export const updateBbItem = ({ fixtureId, bbItemId, attributes }:
{
	fixtureId: number;
	bbItemId: number;
	attributes: {
		type: Values<typeof BareboatItemType>;
		amount: number;
		pcm?: boolean;
		addComm?: number | null;
		brokerPaidByOwnerIds?: Array<number | null>;
		brokerPaidByOwnerPercentages?: Array<number | null>;
		brokerPaidByChartererIds?: Array<number | null>;
		brokerPaidByChartererPercentages?: Array<number | null>;
	};
}) => sendRequest<UpdateBbItemRequest, UpdateBbItemResponse>(
	`/fixtures/${fixtureId}/bb-items/${bbItemId}`,
	'patch',
	{ fixtureId, bbItemId, attributes },
);

export const deleteBbItem = ({ fixtureId, bbItemId }: {
	fixtureId: number; bbItemId: number;
}) => sendRequest<DeleteBbItemRequest, DeleteBbItemResponse>(
	`/fixtures/${fixtureId}/bb-items/${bbItemId}`,
	'delete',
	{ fixtureId, bbItemId },
);

export const getBunkerStems = ({
	voyageId,
	portCallId,
	portCallActionId,
}: {
	voyageId: number;
	portCallId?: number;
	portCallActionId?: number;
}) => sendRequest<GetBunkerStemsRequest, GetBunkerStemsResponse>(
	`/voyages/bunker-stems/${voyageId}`,
	'get',
	{ voyageId, portCallId, portCallActionId },
);

export const createBunkerStem = ({
	voyageId,
	attachments,
	...attributes
}: CreateBunkerStemRequest & { attachments?: any | any[] }) => {
	const data = new FormData();

	const parameters = {
		voyageId,
		...attributes,
		parcels: JSON.stringify(attributes.parcels),
		additionalCosts: JSON.stringify(attributes.additionalCosts),
	};

	// Add all not-null values to form data
	Object.entries(parameters).forEach(([key, value]) => {
		if (value != null) {
			// @ts-ignore
			data.append(key, value);
		}
	});

	if (attachments != null) {
		attachments.forEach((a: any) => data.append('attachments', a));
	}

	return sendRequest('/voyages/bunker-stems/', 'post', data);
};

export const updateBunkerStem = async ({
	id,
	attachments,
	...attributes
}: UpdateBunkerStemRequest & { attachments?: File[] }) => {
	const data = new FormData();

	const parameters = {
		id,
		...attributes,
		parcels: JSON.stringify(attributes.parcels),
		additionalCosts: JSON.stringify(attributes.additionalCosts),
	};

	// Add all not-null values to form data
	Object.entries(parameters).forEach(([key, value]) => {
		if (value != null) {
			// @ts-ignore
			data.append(key, value);
		}
	});

	if (attachments != null) {
		attachments.forEach((a: any) => data.append('attachments', a));
	}

	return sendRequest(`/voyages/bunker-stems/${id}`, 'patch', data);
};

export const deleteBunkerStem = (stemId: number) => sendRequest<DeleteBunkerStemRequest, DeleteBunkerStemResponse>(
	`/voyages/bunker-stems/${stemId}`,
	'delete',
	{ id: stemId },
);

export const getFleetAnalytics = (period: Values<typeof DashboardPeriods>) => sendRequest<
	GetFleetAnalyticsRequest,
	GetFleetAnalyticsResponse
>(
	'/statistics/analytics',
	'get',
	{ period },
);

export const deleteCacheEntry = (endpoint: string) => sendRequest<
	DeleteCacheEntryRequest,
	DeleteCacheEntryResponse
>(
	'/misc/cache',
	'delete',
	{ endpoint },
);

export const getEuas = (voyageId: number) => sendRequest<
	GetEuasRequest,
	GetEuasResponse
>(
	`/voyages/euas/${voyageId}`,
	'get',
	{ voyageId },
);

export const createEua = ({
	values,
	attachments,
}: {
	values: CreateEuaRequest;
	attachments: File[];
}) => sendRequestWithFiles<
	CreateEuaRequest & { attachments?: File[] },
	CreateEuaResponse
>(
	'/voyages/euas',
	'post',
	values,
	attachments,
);

export const updateEua = ({
	id,
	attributes,
	attachments,
}: UpdateEuaRequest & { attachments?: File[] }) => sendRequestWithFiles<
	UpdateEuaRequest,
	UpdateEuaResponse
>(
	`/voyages/euas/${id}`,
	'patch',
	{
		id,
		attributes,
	},
	attachments ?? [],
);

export const deleteEua = (id: number) => sendRequest<
	DeleteEuaRequest,
	DeleteEuaResponse
>(
	`/voyages/euas/${id}`,
	'delete',
	{ id },
);

export const createVoyageExpensePayment = ({
	expenseId,
	paymentDate,
	amount,
}: {
	expenseId: number; paymentDate: Moment; amount: number;
}) => sendRequest<CreateVoyageExpensePaymentRequest, CreateVoyageExpensePaymentResponse>(
	'/voyages/expense-payments',
	'post',
	{ expenseId, paymentDate, amount },
);

export const updateVoyageExpensePayment = ({
	id,
	attributes,
}: { id: number; attributes: UpdateVoyageExpensePaymentRequest['attributes'] }) => sendRequest<
	UpdateVoyageExpensePaymentRequest, UpdateVoyageExpensePaymentResponse>(
	`/voyages/expense-payments/${id}`,
	'patch',
	{ id, attributes },
);

export const deleteVoyageExpensePayment = ({
	id,
}: { id: number }) => sendRequest<DeleteVoyageExpensePaymentRequest, DeleteVoyageExpensePaymentResponse>(
	`/voyages/expense-payments/${id}`,
	'delete',
	{ id },
);

export const getVoyageExpensePayments = (
	expenseId: number,
) => sendRequest<GetVoyageExpensePaymentsRequest, GetVoyageExpensePaymentsResponse>(
	`/voyages/expense-payments/${expenseId}`,
	'get',
	{ expenseId },
);

export const getProject = (voyageId: number) => sendRequest<GetProjectRequest, GetProjectResponse>(
	`/voyages/project/${voyageId}`,
	'get',
	{ voyageId },
);

export const getOperationsData = () => sendRequest<
	GetOpenPositionsDataRequest,
	GetOpenPositionsDataResponse
>(
	'/statistics/open-positions',
	'get',
);

export const getPayablesData = () => sendRequest<
	GetPayablesDataRequest,
	GetPayablesDataResponse
>(
	'/statistics/payables',
	'get',
);

export const updateEstimateGroup = ({
	groupName,
	groupId,
}: {
	groupName: string | null;
	groupId: number;
}) => sendRequest<UpdateEstimateGroupRequest, UpdateEstimateGroupResponse>(
	`/estimates/${groupId}/estimate-group`,
	'patch',
	{
		groupName,
		groupId,
	},
);

export const getPortActivities = () => sendRequest<GetPortActivitiesRequest, GetPortActivitiesResponse>(
	'/port-activities',
	'get',
);

export const createPortActivity = (
	data: CreatePortActivityRequest,
) => sendRequest<CreatePortActivityRequest, CreatePortActivityResponse>(
	'/port-activities',
	'post',
	data,
);

export const updatePortActivity = ({
	id,
	attributes,
}: {
	id: number;
	attributes: UpdatePortActivityRequest['attributes'];
}) => sendRequest<UpdatePortActivityRequest, UpdatePortActivityResponse>(
	`/port-activities/${id}`,
	'patch',
	{ id, attributes },
);

export const deletePortActivity = (
	id: number,
) => sendRequest<DeletePortActivityRequest, DeletePortActivityResponse>(
	`/port-activities/${id}`,
	'delete',
	{ id },
);

export const reorderPortActivities = (ids: number[]) => sendRequest<
	ReorderPortActivitiesRequest,
	ReorderPortActivitiesResponse
>(
	'/port-activities/reorder',
	'patch',
	{ ids },
);

export const getFixingCompanies = () => sendRequest<GetFixingCompaniesRequest, GetFixingCompaniesResponse>(
	'/fixing-companies',
	'get',
);

export const createFixingCompany = (
	data: CreateFixingCompanyRequest,
) => sendRequest<CreateFixingCompanyRequest, CreateFixingCompanyResponse>(
	'/fixing-companies',
	'post',
	data,
	{},
	{ savingIndicator: true },
);

export const updateFixingCompany = (
	id: number,
	attributes: UpdateFixingCompanyRequest['attributes'],
) => sendRequest<UpdateFixingCompanyRequest, UpdateFixingCompanyResponse>(
	`/fixing-companies/${id}`,
	'patch',
	{ id, attributes },
	{},
	{ savingIndicator: true },
);

export const getExclusionItems = (cargoPortId: number) => sendRequest<
	GetExclusionItemsRequest,
	GetExclusionItemsResponse
>(
	`/exclusions/${cargoPortId}`,
	'get',
	{ cargoPortId },
);

export const createExclusionItem = (
	data: CreateExclusionItemRequest,
) => sendRequest<CreateExclusionItemRequest, CreateExclusionItemResponse>(
	'/exclusions',
	'post',
	data,
);

export const updateExclusionItem = ({
	id,
	attributes,
}: {
	id: number;
	attributes: UpdateExclusionItemRequest['attributes'];
}) => sendRequest<UpdateExclusionItemRequest, UpdateExclusionItemResponse>(
	`/exclusions/${id}`,
	'patch',
	{ id, attributes },
);

export const deleteExclusionItem = (
	id: number,
) => sendRequest<DeleteExclusionItemRequest, DeleteExclusionItemResponse>(
	`/exclusions/${id}`,
	'delete',
	{ id },
);

export const calculateLaytime = (cargoId: number) => sendRequest<CalculateLaytimeRequest, CalculateLaytimeResponse>(
	'/laytimes/calculate',
	'post',
	{ cargoId },
);

export const copyLaytimeToCargoRelet = (cargoId: number) => sendRequest<CopyLaytimeToCargoReletRequest, CopyLaytimeToCargoReletResponse>(
	'/laytimes/copy-to-relet',
	'post',
	{ cargoId },
);

export const generateLaytimePdf = ({
	cargoId,
	selectedCargoPortId,
	excludeTimesheet,
}: {
	cargoId: number;
	excludeTimesheet: boolean;
	selectedCargoPortId?: number | undefined;
}) => sendRequest<GenerateLaytimePdfRequest, GenerateLaytimePdfResponse>(
	'/laytimes/pdf',
	'post',
	{ cargoId, selectedCargoPortId, excludeTimesheet },
	{ responseType: 'blob' },
);

export const generateVcPnlPdf = ({
	voyageId,
}: {
	voyageId: number;
}) => sendRequest<GenerateVcPdfRequest, GenerateVcPdfResponse>(
	'/voyages/vc-pnl-pdf',
	'post',
	{ voyageId },
	{ responseType: 'blob' },
);

export const splitCargo = ({
	rows,
	cargoId,
	cargoPortId,
}: {
	rows: Array<SplitRow>;
	cargoId: number;
	cargoPortId: number;
}) => sendRequest<SplitCargoRequest, SplitCargoResponse>(
	'/cargos/split',
	'post',
	{ rows, cargoId, cargoPortId },
	{},
	{ savingIndicator: true }
);

export const undoSplitCargo = ({
	cargoId,
	cargoPortId,
}: {
	cargoId: number;
	cargoPortId: number
}) => sendRequest<UndoSplitCargoRequest, UndoSplitCargoResponse>(
	'/cargos/undo-split',
	'post',
	{ cargoId, cargoPortId },
	{},
	{ savingIndicator: true }
);

export * from './untyped-api';
export * from './sendRequest';
